import type { LanguageModel } from 'ai';
import { z } from 'zod';

import { AD_SPECS } from '@/constants/ad-specs';
import { findUnstatedClaimsCrossModel } from '@/lib/ad-copy/cross-model-claims-check';
import { MAX_COPY_REGENERATIONS } from '@/lib/ad-copy/regeneration-budget';
import {
  holdsFlaggedPhrase,
  withholdFlaggedSentences,
  withoutFlaggedPhrases,
} from '@/lib/ad-copy/withhold-flagged-sentences';
import { repairAdCopy } from '@/lib/ai-agent/helpers/repair-ad-copy';
import {
  AD_COPY_HOOK_SCORE_BAR,
  type AdCopyReview,
  reviewAdCopy,
  reviewPasses,
} from '@/lib/ai-agent/helpers/review-ad-copy';
import { type AdClaim, citableAdClaims, filterVerifiableEvidence } from '@/lib/briefs/ad-grounding';
import {
  type GoogleRsaAdCopyDraft,
  GoogleRsaAdCopyDraftSchema,
  type GoogleRsaAdCopyOutput,
  type LinkedInAdCopyDraft,
  LinkedInAdCopyDraftSchema,
  type LinkedInAdCopyOutput,
  type MetaAdCopyDraft,
  MetaAdCopyDraftSchema,
  type MetaAdCopyOutput,
  normalizeGoogleRsaAdCopy,
  normalizeLinkedInAdCopy,
  normalizeMetaAdCopy,
} from '@/lib/briefs/ad-schemas';
import type { GroundingBundle, PriorAdGround } from '@/lib/briefs/grounding';
import { buildLearningsCopyContext } from '@/lib/campaigns/review-agent/learnings';
import {
  type AdContractOptions,
  type AdContractWarning,
  adviseAdContract,
  adviseGoogleRsaAdContract,
  adviseLinkedInAdContract,
  checkAdContract,
  checkGoogleRsaAdContract,
  checkLinkedInAdContract,
} from '@/lib/content-doctrine/ad-contract';
import { findInventedCausation } from '@/lib/content-doctrine/ad-copy-causation';
import {
  findCompetingAsks,
  findDroppedHedges,
  findInventedOffers,
  findNamedOfficialsInPaidCopy,
  findReaderDiagnosis,
  findSelfReportedStatisticMisuse,
  findSourceHeadlineRestated,
} from '@/lib/content-doctrine/ad-copy-claims';
import { findFiguresOutsideConceptEvidence } from '@/lib/content-doctrine/ad-copy-concept-figures';
import {
  ANTI_CORNY_RULES,
  COPY_BRIEF_LIMITS,
  COPY_BRIEF_RULES,
  EVIDENCE_IS_FACT_NOT_PHRASING,
  GOOGLE_RSA_CRAFT_RULES,
  HOOK_ANTI_PATTERNS,
  HOOK_FRAMEWORKS,
  HOOK_FRAMEWORK_DESCRIPTIONS,
  HOOK_FRAMEWORK_WORKED_EXAMPLES,
  type HookFrameworkId,
  LINKEDIN_CRAFT_RULES,
  META_CRAFT_RULES,
  PAID_CLAIMS_RULES,
  SHARED_CRAFT_RULES,
  buildAngleBlock,
  buildAudienceBlock,
  buildBrandVoiceBlock,
  buildSwapTestBlock,
  findBannedProductLanguage,
  leadHeadlineRoleForFramework,
  renderVoiceRules,
} from '@/lib/content-doctrine/ad-copy-doctrine';
import {
  findGenericAdLead,
  findHeadlineOffConcept,
  statedLeadSpecifics,
} from '@/lib/content-doctrine/ad-copy-lead-specificity';
import {
  findDescriptionRepeatingIntro,
  findUngrammaticalSourceLifts,
} from '@/lib/content-doctrine/ad-copy-lift-and-repeat';
import { findLinkedInImperativeCta } from '@/lib/content-doctrine/ad-copy-linkedin-cta';
import {
  ANTI_CORNY_CHECK_IDS,
  type AdReadinessCheckId,
  checkAdReadiness,
} from '@/lib/content-doctrine/ad-readiness';
import { unsupportedPriceCuts } from '@/lib/content-doctrine/claim-extractors';
import {
  composeTileHeadline,
  conceptOwnResultTiles,
  findUnattributedResultFigures,
  isResultTile,
  isTileRelevantTo,
  stripTileMultiplier,
} from '@/lib/content-doctrine/result-tile-attribution';
import {
  type MoneyOrPercentSpan,
  extractFigures,
  figureIsMoneyOrPercent,
  findUnevidencedFigures,
  moneyOrPercentSpans,
} from '@/lib/content-doctrine/unevidenced-figures';
import { safeGenerateObject } from '@/lib/errors/safe-generate-object';
import type { IterationDiagnosisBranch } from '@/lib/paid-media/iterate-ad-diagnosis';
import {
  type StructuredAdReview,
  toStructuredAdReview,
} from '@/lib/paid-media/structured-ad-review';

/**
 * The corny-gate checks run INSIDE generation (below), never only at a later
 * launch-review call site — see the 2026-09-03 finding this fixes: a hand-
 * written self-referential paragraph shipped twice because `ad-readiness.ts`
 * only ever ran on an already-assembled ad, long after copy generation had
 * finished. `creative_promise_echoed` is included alongside the six
 * `ANTI_CORNY_CHECK_IDS` for the same reason — both are countable, both are
 * checkable the moment the copy exists, neither needs a real creative or
 * destination on file yet.
 */
const GENERATION_GATE_CHECK_IDS: ReadonlySet<AdReadinessCheckId> = new Set([
  ...ANTI_CORNY_CHECK_IDS,
  'creative_promise_echoed',
]);

/** Bounded, per `ad-copy-doctrine.ts`'s standing rule: a gate that regenerates without a bound is an unbounded spend, not a quality gate. */
const MAX_ANTI_CORNY_REGENERATIONS = 2;

/**
 * The audience temperature the whole brief is written against
 * (`COPY_BRIEF_RULES`: "Assume cold unless the direction says the reader
 * already knows this brand"). It is passed to `checkAdReadiness` because
 * `corny_cta_mismatched_to_temperature` fires ONLY on a prospecting or
 * Advantage+ audience: without it, that check was in
 * `GENERATION_GATE_CHECK_IDS` and could never fire, so the gate advertised
 * six anti-corny checks and ran five, and a generated "Shop Now" on a cold
 * ad passed silently. Generation has no audience object to read — it is
 * upstream of the ad set — so the brief's own stated default is the honest
 * value to assume here, and it is the same value `copy-suggestions-core.ts`
 * already passes when it runs the same checks on the finished candidates.
 */
const GENERATION_GATE_AUDIENCE_MODE = 'prospecting' as const;

/**
 * Runs `checkAdReadiness` over every (primary text, headline) PAIR a
 * suggestion candidate could actually ship as — `copy-suggestions-core.ts`
 * pairs both primary-text shapes against both headline roles, so checking
 * only `headlines[0]` left headline 2 unchecked for self-referential
 * narration in the copy body it ships beside. Placeholder creative and
 * destination values are deliberate: this call only ever wants the corny +
 * creative-echo findings, never the unrelated assembly checks ("no
 * creative", "no destination") a candidate mid-generation has not resolved
 * yet. Deduplicated by (id, detail) so a finding present in more than one
 * pair is not reported twice.
 */
function evaluateGenerationGate(
  copy: GroundedMetaAdCopy,
  creativeDescription: string | undefined,
  evidenceOnFile: readonly string[],
): GenerationGateNote[] {
  const findings: GenerationGateNote[] = [];
  const seen = new Set<string>();
  const primaryTexts = [copy.primaryTexts[0], copy.primaryTexts[1]];
  const headlines = [copy.headlines[0], copy.headlines[1]];
  for (const primary of primaryTexts) {
    for (const headline of headlines) {
      const report = checkAdReadiness(
        {
          primaryText: primary.text,
          headline: headline?.text ?? '',
          description: copy.description,
          cta: copy.cta,
          audienceMode: GENERATION_GATE_AUDIENCE_MODE,
          creativeRef: 'generation-gate-placeholder',
          destinationUrl: 'https://placeholder.invalid/generation-gate-placeholder',
          creativeDescription: creativeDescription ?? null,
        },
        // A citable USP is not narration — see `isNarrationCitedAsEvidence`
        // in `ad-readiness.ts`. Without this, the generation-time gate could
        // never learn a candidate's narration IS the brand's own sourced
        // claim, and would keep spending regenerations trying to rewrite
        // away the one differentiator a competitor cannot also claim.
        { evidenceOnFile },
      );
      for (const finding of report.findings) {
        if (!GENERATION_GATE_CHECK_IDS.has(finding.id)) continue;
        const key = `${finding.id}:${finding.detail}`;
        if (seen.has(key)) continue;
        seen.add(key);
        findings.push({
          detail: finding.detail,
          fix: finding.fix,
          regenerate: !REPORT_ONLY_GATE_CHECK_IDS.has(finding.id),
        });
      }
    }
  }
  return findings;
}

/**
 * One thing the gate found wrong with a candidate, in the operator's words.
 *
 * Kept separate from `AdReadinessFinding` on purpose: `ad-readiness.ts` owns
 * a closed set of check ids, and borrowing one of them to report a different
 * defect (banned language, an em dash in a headline) would make its findings
 * mean two things.
 */
export interface GenerationGateNote {
  readonly detail: string;
  readonly fix: string;
  /**
   * Whether a finding is worth spending a frontier generation on.
   *
   * Every finding is REPORTED; only some are worth rewriting the whole ad
   * over. The CTA-temperature check is the case that made the distinction
   * necessary: the CTA is a picklist enum, not prose, an operator changes it
   * in one click, and regenerating both primary texts, both headlines and
   * the description to swap one enum is disproportionate — up to two
   * frontier calls to fix a dropdown. So it is graded, named, and left for
   * the human, while the prose findings (narration, em dashes, a missing
   * mechanism, banned language) regenerate, because those can only be fixed
   * by writing the copy again.
   */
  readonly regenerate: boolean;
}

/**
 * Findings the gate reports but does not rewrite the ad over. See
 * `GenerationGateNote.regenerate`.
 */
const REPORT_ONLY_GATE_CHECK_IDS: ReadonlySet<AdReadinessCheckId> = new Set([
  'corny_cta_mismatched_to_temperature',
]);

/**
 * House-banned product language, applied to every Meta field that ships,
 * because a banned word in a headline costs exactly what it costs in the
 * body.
 */
function evaluateBannedLanguageGate(copy: GroundedMetaAdCopy): GenerationGateNote[] {
  const seen = new Set<string>();
  const notes: GenerationGateNote[] = [];
  for (const hit of findBannedProductLanguage([
    { field: 'primary text 1', text: copy.primaryTexts[0]?.text },
    { field: 'primary text 2', text: copy.primaryTexts[1]?.text },
    { field: 'headline 1', text: copy.headlines[0]?.text },
    { field: 'headline 2', text: copy.headlines[1]?.text },
    { field: 'description', text: copy.description },
    { field: 'call to action', text: copy.cta },
  ])) {
    const key = `${hit.field}:${hit.match.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    notes.push({
      detail: `The ${hit.field} uses house-banned product language ("${hit.match}") — it claims a capability the copy has not shown.`,
      fix: hit.fix,
      regenerate: true,
    });
  }
  return notes;
}

/**
 * The house em-dash rule, applied to the fields `ad-readiness.ts` does not
 * read. Its `corny_em_dash_present` check inspects `ad.primaryText` only, so
 * a report that says "em dashes: 0" was an observation about one field, not
 * a property of the ad. Headlines and the description ship too, and an em
 * dash costs exactly what it costs in the body.
 */
function evaluateEmDashGate(copy: GroundedMetaAdCopy): GenerationGateNote[] {
  const notes: GenerationGateNote[] = [];
  for (const { field, text } of [
    { field: 'headline 1', text: copy.headlines[0]?.text },
    { field: 'headline 2', text: copy.headlines[1]?.text },
    { field: 'description', text: copy.description },
  ]) {
    if (!text?.includes('—')) continue;
    notes.push({
      detail: `The ${field} uses an em dash. Mora's own house rule is none in ad copy, ever.`,
      fix: `Rewrite the ${field} with a comma, a period, or a colon instead of the em dash.`,
      regenerate: true,
    });
  }
  return notes;
}

/** One shipping field, ready to run every figure/qualifier/promise check over. */
export interface AdCopyShippingField {
  readonly field: string;
  readonly text: string;
}

/**
 * The fields a finished ad actually ships, one list per platform shape —
 * shared by every post-generation quality check in this module
 * (`findUnevidencedFigures`, `evaluateQualityDoctrine`) so there is exactly
 * ONE place that knows which fields exist per platform, not one per check.
 */
function shippingFields(
  adPlatform: GroundedAdCopyPlatform,
  copy: GroundedMetaAdCopy | GroundedLinkedInAdCopy | GroundedGoogleRsaAdCopy,
): AdCopyShippingField[] {
  if (adPlatform === 'google_rsa') {
    const rsa = copy as GroundedGoogleRsaAdCopy;
    return [
      ...rsa.headlines.map((headline, index) => ({
        field: `headline ${index + 1}`,
        text: headline.text,
      })),
      ...rsa.descriptions.map((description, index) => ({
        field: `description ${index + 1}`,
        text: description.text,
      })),
    ];
  }
  if (adPlatform === 'linkedin') {
    const linkedIn = copy as GroundedLinkedInAdCopy;
    const fields: AdCopyShippingField[] = [
      { field: 'commentary', text: linkedIn.commentary },
      { field: 'headline', text: linkedIn.headline },
    ];
    if (linkedIn.description) fields.push({ field: 'description', text: linkedIn.description });
    return fields;
  }
  const meta = copy as GroundedMetaAdCopy;
  return [
    { field: 'primary text 1', text: meta.primaryTexts[0]?.text ?? '' },
    { field: 'primary text 2', text: meta.primaryTexts[1]?.text ?? '' },
    { field: 'headline 1', text: meta.headlines[0]?.text ?? '' },
    { field: 'headline 2', text: meta.headlines[1]?.text ?? '' },
    { field: 'description', text: meta.description },
  ];
}

/**
 * Reports every figure in the shipping copy that does not appear anywhere in
 * the evidence the caller authorized.
 *
 * This is an ADVISORY, not a `needs_review` reason and not a regeneration
 * trigger, and the distinction is deliberate on both counts. A figure about
 * the reader's own week ("four weeks of invoices") is legitimate copy and
 * not a product claim, so a gate that regenerated on it would fight the
 * brief and spend real model calls doing it; a `needs_review` flag that
 * fires on every ad containing any number teaches a marketer to ignore the
 * flag, which is the same harm as a false one. What a buyer actually wants
 * is every number in the ad sourced before the money goes out, which is what
 * this line gives them.
 *
 * Runs over EVERY platform's shipping fields, not only Meta's — a LinkedIn
 * or Google RSA figure with nothing behind it is exactly as unsourced.
 *
 * Lane R2-3: the actual figure-extraction/checking logic lives in
 * `content-doctrine/unevidenced-figures.ts` — shared with the organic
 * companion post, never forked — this is a thin wrapper that keeps this
 * module's existing call-site shape (`GroundedAdCopyWarning[]`, no
 * `provenance` argument) unchanged.
 */
function findUnevidencedFiguresInAdCopy(
  fields: readonly AdCopyShippingField[],
  evidenceCorpus: string,
): GroundedAdCopyWarning[] {
  return findUnevidencedFigures(
    fields,
    evidenceCorpus,
    'src/lib/ad-copy/generate-grounded-ad-copy.ts — every digit and spelled-out number in the shipping fields, checked against the caller-authorized evidence. An observation, not a defect.',
  );
}

/**
 * The BOUNDED scoping-qualifier vocabulary a figure's own evidence sentence
 * may carry — per the founder's 2026-09-24 instruction, deliberately not
 * open-ended. Two categories the copy must ECHO when the evidence states
 * them (`findMissingScopingQualifiers`), plus a third (`scope`) that marks a
 * figure as a BRAND-WIDE statistic rather than one measured for a specific
 * scenario (`findScenarioScopedFigures`).
 */
const MAGNITUDE_QUALIFIER_DEFS: ReadonlyArray<{ label: string; re: RegExp }> = [
  { label: 'on average', re: /\bon average\b/i },
  { label: 'average', re: /\baverage\b/i },
  { label: 'median', re: /\bmedian\b/i },
  { label: 'typical', re: /\btypical(?:ly)?\b/i },
  { label: 'up to', re: /\bup to\b/i },
  { label: 'as much as', re: /\bas much as\b/i },
];
/**
 * "$6M a year", "$6M annually", "$6M per year", "$6M/yr" and "$6M annual" state the same
 * recurrence, so any of them satisfies an evidence sentence that scopes a figure by any of them.
 */
const RECURRENCE_FORM =
  /\bannual(?:ly)?\b|\byearly\b|\b(?:per|a|each|every)\s+(?:year|annum|yr)\b|\/\s?(?:yr|year)\b|\bp\.a\./i;
const PERIOD_QUALIFIER_DEFS: ReadonlyArray<{ label: string; re: RegExp }> = [
  { label: 'first-year', re: /\bfirst[- ]year\b/i },
  { label: 'per year', re: /\bper year\b/i },
  {
    label: 'annual',
    re: /\bannual(?:ly)?\b|\byearly\b|\b(?:a|each|every)\s+year\b|\bper\s+annum\b/i,
  },
];
const SCOPE_QUALIFIER_DEFS: ReadonlyArray<{ label: string; re: RegExp }> = [
  { label: 'across … portfolios', re: /\bacross\b[\s\S]{0,60}\bportfolios?\b/i },
];

function matchedQualifierLabels(
  text: string,
  defs: ReadonlyArray<{ label: string; re: RegExp }>,
): string[] {
  const labels: string[] = [];
  for (const def of defs) {
    if (def.re.test(text)) labels.push(def.label);
  }
  return labels;
}

/** The first caller-authorized source sentence that carries this exact figure, or null. */
function findEvidenceSentenceForFigure(
  figure: number,
  verifiableSources: readonly string[],
): string | null {
  let bare: string | null = null;
  for (const source of verifiableSources) {
    if (!extractFigures(source).includes(figure)) continue;
    if (figureIsMoneyOrPercent(figure, source)) return source;
    bare ??= source;
  }
  return bare;
}

/** `43` → `"43%"` when the field text writes it as a percentage, else `"43"`. */
function figureDisplay(figure: number, text: string): string {
  return new RegExp(`${figure}\\s*%`).test(text) ? `${figure}%` : `${figure}`;
}

function formatFieldLabel(field: string): string {
  return field.length > 0 ? field.charAt(0).toUpperCase() + field.slice(1) : field;
}

/**
 * The rewrite direction the judge's verdict hands the next regeneration. The
 * judge may score a hook under the bar (or call it generic) while listing no
 * `failsBecause` at all; an empty note list would make the "rewrite" a blind
 * re-draw, so the weakest line it named becomes the direction instead.
 */
function unsupportedAssertionNotes(review: AdCopyReview): string[] {
  return (review.unsupportedAssertions ?? []).map(
    (assertion) =>
      `Cut or replace this sentence, which asserts something the evidence does not state: "${assertion}". Say only what a line in the evidence says, and let the reader draw the conclusion.`,
  );
}

function judgeRewriteNotes(review: AdCopyReview): string[] {
  const unsupported = unsupportedAssertionNotes(review);
  if (review.failsBecause.length > 0) return [...review.failsBecause, ...unsupported];
  if (unsupported.length > 0 && review.hookScore >= AD_COPY_HOOK_SCORE_BAR && !review.isGeneric) {
    return unsupported;
  }
  return [
    `Rewrite the weakest line so it states a specific, sourced fact instead of a generic claim a competitor could reuse: "${review.weakestLine}"`,
    ...unsupported,
  ];
}

/** A finding as each `find*` check produces it — `evaluateQualityDoctrine` stamps `blocking`. */
type UnflaggedDoctrineFinding = Omit<QualityDoctrineFinding, 'blocking'>;

export interface QualityDoctrineFinding {
  /** What ships in `needs_review`'s `detail` if this survives the one targeted repair. */
  readonly detail: string;
  /** The positive instruction handed to the targeted regeneration — never a bare "don't do X" ban. */
  readonly fix: string;
  /**
   * True for a CLAIMS defect (an unqualified statistic, an outcome promise,
   * a legal/directive claim the evidence never made, a brand's own avoided
   * phrase). A blocking finding that survives the shared regeneration budget
   * means the copy does NOT ship — see the claims gate after the advisory
   * repair in `generateGroundedAdCopy`. False for a QUALITY defect (a
   * generic LinkedIn hook), which ships flagged `needs_review` instead.
   */
  readonly blocking: boolean;
  /** The shipping field the finding is about, when it is about exactly one. */
  readonly field?: string;
  /** The one sentence that carries the defect, when there is exactly one. */
  readonly sentence?: string;
  /** A replacement for `sentence` built only from the evidence, when the defect has a mechanical fix. */
  readonly rewrite?: string;
}

/**
 * MISSING SCOPING QUALIFIERS. When a figure's own evidence sentence scopes
 * it — "an AVERAGE 43% FIRST-YEAR reduction" — the shipped field using that
 * figure must carry the SAME scoping, not just the bare number. A sourced
 * number stripped of its qualifier is a stronger, unsupported claim wearing
 * a sourced one's clothes: "43%" alone promises 43% to every reader, where
 * "an average 43%, in the first year" promises something much narrower and
 * true. Real fixture, 2026-09-24 (the persisted ad row 9155e7a5 on brief a30e05c0):
 * evidence "an average 43% first-year tax reduction across complex,
 * capital-intensive portfolios nationwide", headline "Cut taxes ... by
 * 43%." with neither qualifier restated.
 */
export function findMissingScopingQualifiers(
  fields: readonly AdCopyShippingField[],
  verifiableSources: readonly string[],
): UnflaggedDoctrineFinding[] {
  const findings: UnflaggedDoctrineFinding[] = [];
  const sourceSpans = verifiableSources.map((source) => ({
    source,
    spans: moneyOrPercentSpans(source),
  }));
  for (const { field, text } of fields) {
    if (!text) continue;
    const reported = new Set<string>();
    for (const span of moneyOrPercentSpans(text)) {
      const key = `${span.kind}:${span.value}`;
      if (reported.has(key)) continue;
      const evidence = sourceSpans.find((entry) =>
        entry.spans.some((other) => other.kind === span.kind && other.value === span.value),
      );
      if (!evidence) continue; // an unevidenced figure is `findUnevidencedFigures`'s finding, not this one.
      const target = evidence.spans.filter(
        (other) => other.kind === span.kind && other.value === span.value,
      );

      const evidenceMagnitude = qualifiersOfFigure(
        evidence.source,
        evidence.spans,
        target,
        MAGNITUDE_QUALIFIER_DEFS,
      );
      const evidencePeriod = qualifiersOfFigure(
        evidence.source,
        evidence.spans,
        target,
        PERIOD_QUALIFIER_DEFS,
      );
      if (evidenceMagnitude.length === 0 && evidencePeriod.length === 0) continue;

      const fieldMagnitude = matchedQualifierLabels(text, MAGNITUDE_QUALIFIER_DEFS);
      const missing: string[] = [];
      if (evidenceMagnitude.length > 0 && fieldMagnitude.length === 0) {
        missing.push(evidenceMagnitude[0]!);
      }
      const needsFirstYear = evidencePeriod.includes('first-year');
      const needsRecurrence = evidencePeriod.some((label) => label !== 'first-year');
      if (needsFirstYear && !PERIOD_QUALIFIER_DEFS[0]!.re.test(text)) missing.push('first-year');
      if (needsRecurrence && !RECURRENCE_FORM.test(text)) {
        missing.push(evidencePeriod.find((label) => label !== 'first-year')!);
      }
      if (missing.length === 0) continue;

      reported.add(key);
      const display = span.text;
      findings.push({
        field,
        detail: `${formatFieldLabel(field)} uses ${display} without "${missing.join(' ')}" as the brand profile states it.`,
        fix: `In ${field}, restate ${display} exactly as the evidence scopes it — include ${missing.map((label) => `"${label}"`).join(' and ')} (any wording of the same period works: "a year", "per year", "annually"). The evidence says: "${evidence.source}"`,
      });
    }
  }
  return findings;
}

/**
 * The qualifiers in `source` that scope THIS figure: each qualifier belongs to the money or
 * percent figure nearest to it, so "40% reduction. $6M annually" hands "annually" to the $6M
 * only, and the 40% is not asked to carry it.
 */
function qualifiersOfFigure(
  source: string,
  spans: readonly MoneyOrPercentSpan[],
  target: readonly MoneyOrPercentSpan[],
  defs: ReadonlyArray<{ label: string; re: RegExp }>,
): string[] {
  const labels: string[] = [];
  const gap = (a: { start: number; end: number }, b: { start: number; end: number }): number =>
    Math.max(0, a.start - b.end, b.start - a.end);
  for (const def of defs) {
    const match = new RegExp(def.re.source, 'i').exec(source);
    if (!match) continue;
    const at = { start: match.index, end: match.index + match[0].length };
    const nearestOverall = Math.min(...spans.map((span) => gap(at, span)));
    const nearestTarget = Math.min(...target.map((span) => gap(at, span)));
    if (nearestTarget <= nearestOverall) labels.push(def.label);
  }
  return labels;
}

/**
 * SCENARIO-SCOPED BRAND STATISTIC. A figure whose evidence sentence marks it
 * as a brand-wide number (an "across ... portfolios" scope) must not be
 * re-attached, in the SAME clause, to a scenario word that appears only in
 * this request's own direction and nowhere in anything the account actually
 * grounded — that is exactly how a true, sourced percentage becomes a false,
 * unmeasured claim. Real fixture, 2026-09-24 (the persisted ad row 9155e7a5): evidence
 * "43% average first-year tax reduction across complex, capital-intensive
 * portfolios nationwide"; shipped headline "Cut taxes on UNPOWERED SITES by
 * 43%" — "unpowered" names the news scenario the direction carried, never
 * measured by anything the evidence states.
 *
 * Deliberately narrow, and paired with a judge-rubric item
 * (`AD_COPY_ADDITIONAL_JUDGE_CHECKS`) that can catch what this heuristic
 * cannot: this only flags a scenario WORD that is unique to the direction
 * (absent from every grounded source), six-plus letters so it skips
 * ordinary short connective words, and sharing a clause with the figure.
 */
function findScenarioScopedFigures(
  fields: readonly AdCopyShippingField[],
  verifiableSources: readonly string[],
  direction: string | undefined,
): UnflaggedDoctrineFinding[] {
  if (!direction?.trim()) return [];

  const contentWords = (text: string): string[] =>
    text
      .toLowerCase()
      .replace(/[^\p{L}\s]/gu, ' ')
      .split(/\s+/)
      .filter((word) => word.length >= 6);

  const groundedWords = new Set(verifiableSources.flatMap((source) => contentWords(source)));
  const scenarioWords = new Set(contentWords(direction).filter((word) => !groundedWords.has(word)));
  if (scenarioWords.size === 0) return [];

  const findings: UnflaggedDoctrineFinding[] = [];
  for (const { field, text } of fields) {
    if (!text) continue;
    for (const figure of extractFigures(text)) {
      const evidenceSentence = findEvidenceSentenceForFigure(figure, verifiableSources);
      if (!evidenceSentence) continue;
      if (matchedQualifierLabels(evidenceSentence, SCOPE_QUALIFIER_DEFS).length === 0) continue;

      const clauses = text.split(/[.!?]/);
      const clause = clauses.find((c) => extractFigures(c).includes(figure)) ?? text;
      const scenarioWord = contentWords(clause).find((word) => scenarioWords.has(word));
      if (!scenarioWord) continue;

      const display = figureDisplay(figure, text);
      findings.push({
        field,
        detail: `${formatFieldLabel(field)} ties ${display} — a brand-wide figure — to "${scenarioWord}", a scenario the evidence never measured.`,
        fix: `In ${field}, state ${display} exactly as the evidence scopes it, without tying it to "${scenarioWord}" — that scenario is not what was measured.`,
      });
    }
  }
  return findings;
}

/**
 * SECOND-PERSON OUTCOME PROMISES. "You secure a 43% average first-year
 * liability reduction" tells THIS reader they personally will get 43% — a
 * guarantee the evidence (an account-wide average) cannot make about any one
 * reader. `checkEvidenceSafetyFloor`'s `UNSUPPORTED_CERTAINTY_PATTERN`
 * already hard-blocks the literal word "guarantee(d)" on every platform;
 * this catches the promise-shaped SENTENCE that says the same thing without
 * the word, which nothing else in the pipeline reads. Real fixture,
 * 2026-09-24 (the persisted ad row fd323b07 on brief a30e05c0): "You secure a 43%
 * average first-year liability reduction because we reject standard county
 * tables...".
 */
const PROMISE_VERB_PATTERN =
  /\byou(?:'ll| will)? (?:secure|get|receive|save|achieve)\b[^.]{0,40}\d/i;
const GUARANTEE_WORD_PATTERN = /\bguarantee(?:d)?\b/i;

function findOutcomeGuaranteePromises(
  fields: readonly AdCopyShippingField[],
): UnflaggedDoctrineFinding[] {
  const findings: UnflaggedDoctrineFinding[] = [];
  for (const { field, text } of fields) {
    if (!text) continue;
    const promiseMatch = text.match(PROMISE_VERB_PATTERN);
    if (promiseMatch) {
      findings.push({
        field,
        detail: `${formatFieldLabel(field)} promises the reader a personal outcome ("${promiseMatch[0]}") the evidence does not make about any one reader.`,
        fix: `Rewrite ${field} so it never tells the reader what THEY will secure, get, receive, save, or achieve — state what the product/service does, in third person, instead.`,
      });
      continue; // one finding per field is enough to drive the repair note.
    }
    const guaranteeMatch = text.match(GUARANTEE_WORD_PATTERN);
    if (guaranteeMatch) {
      findings.push({
        field,
        detail: `${formatFieldLabel(field)} uses "${guaranteeMatch[0]}" — an outcome guarantee the evidence does not support.`,
        fix: `Rewrite ${field} without "${guaranteeMatch[0]}" — state the evidenced fact instead of promising a guaranteed result.`,
      });
    }
  }
  return findings;
}

/**
 * BRAND-FORBIDDEN PHRASE SHIPPED. `buildBrandVoiceBlock` already tells the
 * generation prompt "do not use any of them in any field" for every phrase in
 * `brand.avoidPhrases` — but that is a request the model can (and, in the
 * round-2 fixture below, did) ignore, and nothing downstream ever checked the
 * OUTPUT against the same list. A prose ask with no code-level gate is not a
 * gate — `feedback_rules_that_must_hold_go_in_code_not_prose.md`. This
 * function is the gate: a brand's own forbidden phrase is checked
 * deterministically against every shipping field, exactly like the house-
 * banned-word gate (`evaluateBannedLanguageGate`) does for the universal
 * list, except this list is per-workspace instead of global. Real fixture,
 * 2026-09-24/25 (ITC Tax round-2 run): the workspace's claims doctrine marks
 * "obsolescence" claims (e.g. "stalled projects qualify for obsolescence") as
 * a banned, unverified legal/directive claim, but nothing enforced that once
 * generation produced "environmental pauses create claimable obsolescence" —
 * a functionally identical claim in different words. This gate does not know
 * the word "obsolescence"; it only knows what THIS workspace's own
 * `avoidPhrases` list says is forbidden — never special-cased to one
 * account, one vertical, or one article.
 */
function findBannedPhrasesShipped(
  fields: readonly AdCopyShippingField[],
  avoidPhrases: readonly string[],
): UnflaggedDoctrineFinding[] {
  const findings: UnflaggedDoctrineFinding[] = [];
  const phrases = avoidPhrases.filter((phrase) => phrase.trim().length > 0);
  if (phrases.length === 0) return findings;

  for (const { field, text } of fields) {
    if (!text) continue;
    for (const phrase of phrases) {
      const trimmed = phrase.trim();
      if (!trimmed) continue;
      // Whole-word-ish, case-insensitive containment — a brand forbids the
      // CONCEPT ("obsolescence"), not one exact sentence, so this matches the
      // phrase anywhere it appears in the field, not just as a full-field
      // equality check.
      const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(escaped, 'i');
      if (re.test(text)) {
        findings.push({
          field,
          detail: `${formatFieldLabel(field)} uses "${trimmed}" — a phrase this brand's own workspace settings forbid.`,
          fix: `Rewrite ${field} without "${trimmed}" or anything that restates the same claim in different words — that concept is banned for this brand, not just that exact wording.`,
        });
        break; // one finding per (field, phrase) is enough; move to the next phrase.
      }
    }
  }
  return findings;
}

/**
 * UNGROUNDED LEGAL / DIRECTIVE / REGULATORY-OUTCOME CLAIM. A phrase like "you
 * qualify for", "write-down", "obsolescence", or "halts your" tells the
 * reader a specific legal, tax, or regulatory outcome applies to THEM — a
 * claim only the evidence on file, never the model's own inference from the
 * request or direction, can support. The pattern list names the SHAPE of a
 * directive/outcome claim (a verb of legal entitlement, an
 * accounting/regulatory-outcome noun), never one brand's or one vertical's
 * vocabulary — general across any workspace, unlike `findBannedPhrasesShipped`
 * (which only knows a per-brand `avoidPhrases` list). Deterministic: a match
 * ships only when the exact matched phrase also appears in the evidence
 * corpus; otherwise it blocks and regenerates, same discipline as every other
 * doctrine finding in this module. Real fixture, 2026-09-24/25 (ITC Tax
 * round-2): generation produced "stalled projects qualify for obsolescence"
 * and, after a plain prose ask not to, "environmental pauses create claimable
 * obsolescence" — a functionally identical unverified legal/directive claim
 * the article and brand profile never made, and the founder's own claims
 * doctrine (v1 §1) names this exact claim class as must-not-ship.
 * "guaranteed"/"guarantee" is deliberately NOT in this list — it is already a
 * harder, unconditional block via `findOutcomeGuaranteePromises`'s
 * `GUARANTEE_WORD_PATTERN`, and listing it twice would double-report one
 * defect.
 */
export const LEGAL_DIRECTIVE_CLAIM_PATTERNS: ReadonlyArray<{ label: string; re: RegExp }> = [
  { label: 'qualify for', re: /\bqualif(?:y|ies|ying) for\b/i },
  { label: 'eligible for', re: /\beligible for\b/i },
  { label: 'entitled to', re: /\bentitled to\b/i },
  { label: 'exempt from', re: /\bexempt(?:ion)? from\b/i },
  { label: 'write-down', re: /\bwrite[- ]?downs?\b/i },
  { label: 'write-off', re: /\bwrite[- ]?offs?\b/i },
  { label: 'obsolescence', re: /\bobsolescence\b/i },
  { label: 'halts your', re: /\bhalts? your\b/i },
  { label: 'automatically applies', re: /\bautomatically (?:appl(?:y|ies)|qualif(?:y|ies))\b/i },
  { label: 'overpay', re: /\boverpay(?:s|ing|ment)?\b|\bover-?tax(?:ed|es|ing)?\b/i },
  {
    label: 'inflated/overvalued',
    re: /\binflat(?:e|es|ed|ing)\b(?=(?:\W+\w+){0,2}?\W+(?:valu\w*|assess\w*|tax\w*|bills?)\b)|\bovervalu(?:e|es|ed|ation|ations)\b|\bover-?assess(?:ed|ment|ments)?\b/i,
  },
  {
    label: 'aggressive(ly)',
    re: /\baggressive(?:ly)?\b(?=(?:\W+\w+){0,3}?\W+(?:tax\w*|positions?|deduct\w*|filings?|assess\w*|appeals?|valuations?|reductions?|savings?|claims?|refunds?|write[- ]?offs?)\b)/i,
  },
  {
    label: 'will inflate/overstate',
    re: /\b(?:will|would|is going to|are going to)\s+(?:\w+\s+)?(?:inflate|overstate|overvalue)\b/i,
  },
];

function findUngroundedRegulatoryClaims(
  fields: readonly AdCopyShippingField[],
  evidenceCorpus: string,
): UnflaggedDoctrineFinding[] {
  const findings: UnflaggedDoctrineFinding[] = [];
  const normalizedEvidence = evidenceCorpus.toLowerCase();
  for (const { field, text } of fields) {
    if (!text) continue;
    for (const pattern of LEGAL_DIRECTIVE_CLAIM_PATTERNS) {
      const match = text.match(pattern.re);
      if (!match) continue;
      if (normalizedEvidence.includes(match[0].toLowerCase())) continue; // the brand/article itself made this exact claim.
      findings.push({
        field,
        detail: `${formatFieldLabel(field)} makes a legal/directive/outcome claim ("${match[0]}") that does not appear anywhere in the evidence on file — an unverified regulatory promise.`,
        fix: `Rewrite ${field} without "${match[0]}" or any restatement of the same claim in different words — state only what the evidence supports and let the reader draw their own legal or tax conclusion.`,
      });
      break; // one finding per field is enough to drive the repair note.
    }
  }
  return findings;
}

/** How much of a LinkedIn commentary is visible before "see more" truncates it (`AD_SPECS.linkedin.commentary`'s own advisory fold). */
const LINKEDIN_HOOK_FOLD_CHARS = 150;

/**
 * The whole commentary — hook, body and close — the reader sees after tapping
 * "see more". LinkedIn's own 150-character advisory (`AD_SPECS.linkedin.commentary.adviseMax`)
 * is a truncation point, not a length ceiling: 3,000 characters are stored,
 * and the founder-approved reference copy runs ~385-441 characters with its
 * hook ending at the fold. Held to 150 the whole ad is a hook with no
 * body and no close, which is exactly the generic shape the judges failed.
 * Over this length, the ordinary "may truncate" advisory and its one repair
 * pass apply as before.
 */
const LINKEDIN_COMMENTARY_EXPANDED_MAX = 450;
const LINKEDIN_COMMENTARY_TARGET_MIN = 300;

/**
 * `adviseLinkedInAdContract` with the commentary-over-150 advisory lifted for
 * a commentary that fits the expanded budget above — every other advisory
 * (headline/description length, and commentary beyond 450) is untouched.
 */
function adviseLinkedInWithExpandedFold(copy: LinkedInAdCopyOutput): AdContractWarning[] {
  return adviseLinkedInAdContract(copy).filter(
    (warning) =>
      !(
        warning.field === 'commentary' && copy.commentary.length <= LINKEDIN_COMMENTARY_EXPANDED_MAX
      ),
  );
}

/**
 * LINKEDIN HOOK MUST CARRY A SOURCED FACT. LinkedIn shows only the first
 * ~150 characters of `commentary` before "see more" — that fold IS the ad for
 * most impressions. A fold that opens on generic scene-setting ("Are you
 * still capitalizing utility interconnection prepayments...") reads
 * identically for any brand in the vertical, which is exactly the judges'
 * repeated round-1/2/3 finding: "a competitor could run this with the name
 * swapped." Deterministic check, not a sentiment judgement: the fold must
 * contain either a sourced figure that also appears in the evidence, or a
 * 4-plus-word run shared verbatim (case/punctuation-insensitive) with some
 * evidence sentence — the article's own fact, or the brand's own sourced
 * claim, stated up front instead of held for the body.
 */
function findLinkedInHookMissingSourcedFact(
  fields: readonly AdCopyShippingField[],
  verifiableSources: readonly string[],
): UnflaggedDoctrineFinding[] {
  const commentary = fields.find((field) => field.field === 'commentary')?.text;
  if (!commentary?.trim()) return [];
  const hook = commentary.slice(0, LINKEDIN_HOOK_FOLD_CHARS);

  const hookFigures = new Set(extractFigures(hook));
  if (hookFigures.size > 0) {
    for (const source of verifiableSources) {
      if (extractFigures(source).some((figure) => hookFigures.has(figure))) return [];
    }
  }

  const normalizeWords = (text: string): string[] =>
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .split(/\s+/)
      .filter(Boolean);
  const hookText = normalizeWords(hook).join(' ');
  for (const source of verifiableSources) {
    const sourceWords = normalizeWords(source);
    for (let start = 0; start + 4 <= sourceWords.length; start++) {
      const gram = sourceWords.slice(start, start + 4).join(' ');
      if (gram.length > 0 && hookText.includes(gram)) return [];
    }
  }

  return [
    {
      field: 'commentary',
      detail:
        'The commentary hook (the first ~150 characters, before "see more") carries no concrete fact from the evidence — it reads as generic scene-setting a competitor in the same vertical could run unchanged.',
      fix: "Rewrite the opening of commentary so the first ~150 characters state one specific, sourced fact from the evidence (the article's own fact, or the brand's own sourced claim), verbatim or near-verbatim — not a rhetorical question or a generic observation about the reader.",
    },
  ];
}

/**
 * The evidence CLASSES the doctrine pass needs beyond the flat list of
 * citable strings: which of them are the brand's own claims (a statistic in
 * there is the brand's to attribute), which come from the source article (an
 * officeholder's name is learned there), and the article's own title.
 */
export interface DoctrineContext {
  readonly brandSources?: readonly string[];
  readonly otherSources?: readonly string[];
  readonly articleSources?: readonly string[];
  readonly sourceTitle?: string | null;
  readonly brandName?: string | null;
  /** The brand's catalogued product names: not a specific a competitor lacks. */
  readonly productNames?: readonly string[];
  readonly competitorNames?: readonly string[];
  /**
   * What the Concept this copy executes is about (its hook). A client result
   * from another industry is refused as off-topic proof against this. Absent
   * means unknown: nothing is judged.
   */
  readonly conceptSubject?: string | null;
  /**
   * The Concept's own text (hook, summary, proof). A result figure in the copy
   * that is in none of it is refused as borrowed from another result. Absent or
   * empty means unknown: nothing is judged.
   */
  readonly conceptEvidence?: readonly string[];
  /**
   * The page text the paid-media claims check reads: the catalog, the selected product and the
   * destination page, never a customer quote or a third-party article. A price-cut line ("Now $58.00",
   * "$10 off") is judged against this alone. Absent means unknown: nothing is judged.
   */
  readonly pageSources?: readonly string[];
}

/** The price and details of the offer this ad sells are its own, never a figure borrowed from a sibling Concept. */
function isSoldOfferFact(source: string, product: string): boolean {
  const wanted = product.trim().toLowerCase();
  const line = source.trim().toLowerCase();
  return (
    line.startsWith('selected catalog price:') ||
    line.startsWith(`${wanted} (`) ||
    line.startsWith(`${wanted}:`)
  );
}

/**
 * A price-cut line ("Now $58.00", "was $80", "$10 off", "on sale") is written only when the page text
 * carries a compare-at price (and, for a saving, the figure), or says the same phrase itself. This is
 * the claims check's own rule (`unsupportedPriceCuts`), run over every field every platform ships, so
 * the writer is never looser than the check that fails the saved ad. Pool is the page text alone.
 */
function findUnsupportedPriceCuts(
  fields: readonly AdCopyShippingField[],
  pageSources: readonly string[] | undefined,
): UnflaggedDoctrineFinding[] {
  if (pageSources === undefined) return [];
  const poolText = pageSources.join('\n');
  const findings: UnflaggedDoctrineFinding[] = [];
  for (const { field, text } of fields) {
    if (!text) continue;
    for (const { claim, why } of unsupportedPriceCuts(text, poolText)) {
      findings.push({
        field,
        detail: `${formatFieldLabel(field)} says "${claim}" (${why}).`,
        fix: `Rewrite ${field} with the plain price as the evidence states it, or with no price at all, and no "now", "was", "off", "save" or "sale" wording.`,
      });
    }
  }
  return findings;
}

/**
 * ONE combined pass over every deterministic post-generation doctrine rule
 * this module enforces beyond `checkAdContract`/`adviseAdContract` — run
 * BEFORE the one targeted repair regeneration (finds what to fix) and again
 * AFTER it, against whichever candidate actually ships (recomputed, never
 * accumulated — see `gateReasons`'s identical discipline below for why a
 * stale finding must never ride out on copy that no longer has it).
 */
export function evaluateQualityDoctrine(
  fields: readonly AdCopyShippingField[],
  verifiableSources: readonly string[],
  direction: string | undefined,
  avoidPhrases: readonly string[] = [],
  context: DoctrineContext = {},
): QualityDoctrineFinding[] {
  const adPlatform: GroundedAdCopyPlatform = fields.some((field) => field.field === 'commentary')
    ? 'linkedin'
    : fields.some((field) => field.field === 'primary text 1')
      ? 'meta'
      : 'google_rsa';
  const claims: UnflaggedDoctrineFinding[] = [
    ...findMissingScopingQualifiers(fields, verifiableSources),
    ...findScenarioScopedFigures(fields, verifiableSources, direction),
    ...findOutcomeGuaranteePromises(fields),
    ...findBannedPhrasesShipped(fields, avoidPhrases),
    // The claim must be in the SOURCED evidence — never the request's own
    // direction or the Concept's summary, which are model-written and are
    // exactly where an ungrounded "obsolescence" claim comes from.
    ...findUngroundedRegulatoryClaims(fields, verifiableSources.join('\n')),
    ...findSelfReportedStatisticMisuse(fields, {
      brandSources: context.brandSources ?? [],
      otherSources: context.otherSources ?? [],
      brandName: context.brandName ?? null,
    }),
    ...findNamedOfficialsInPaidCopy(fields, context.articleSources ?? []),
    // A hedge or an order in the source, restated flat, is a stronger claim than
    // the source made; an offer the brand never described is a promise it never
    // made; and what the reader's own situation is, nothing on file knows.
    // The brand's own page hedges too: "opportunities your consultant may have missed"
    // restated as "…your consultant missed" is the same dropped hedge.
    ...findDroppedHedges(fields, [
      ...(context.articleSources ?? []),
      ...(context.brandSources ?? []),
    ]),
    ...findInventedOffers(
      fields,
      context.brandSources ?? [],
      context.brandName ?? null,
      verifiableSources,
    ),
    ...findUnattributedResultFigures(
      fields,
      verifiableSources,
      context.brandName ?? null,
      context.conceptSubject ?? null,
    ),
    ...findFiguresOutsideConceptEvidence(fields, context.conceptEvidence ?? []),
    // Two source facts joined by a cause the source never stated.
    ...findInventedCausation(fields, verifiableSources),
    // The Concept's swap-test bar: the headline and the opening line must say
    // something this brand's evidence says. Generic copy is never ready.
    ...findGenericAdLead(
      fields,
      verifiableSources,
      {
        brandName: context.brandName ?? null,
        productNames: context.productNames ?? [],
        competitorNames: context.competitorNames ?? [],
      },
      adPlatform,
    ),
    // The button carries the ask: an instruction in the intro is a second one.
    ...findLinkedInImperativeCta(fields, adPlatform),
    ...findDescriptionRepeatingIntro(fields, context.brandName ?? null, adPlatform),
    ...findUngrammaticalSourceLifts(fields, verifiableSources),
    ...findCompetingAsks(fields),
    ...findReaderDiagnosis(fields),
    ...findUnsupportedPriceCuts(fields, context.pageSources),
  ];
  const quality: UnflaggedDoctrineFinding[] = [
    ...findSourceHeadlineRestated(fields, context.sourceTitle),
    ...findHeadlineOffConcept(fields, context.conceptSubject, adPlatform),
  ];
  return [
    ...claims.map((finding) => ({ ...finding, blocking: true })),
    ...quality.map((finding) => ({ ...finding, blocking: false })),
    ...findLinkedInHookMissingSourcedFact(fields, verifiableSources).map((finding) => ({
      ...finding,
      blocking: false,
    })),
  ];
}

export type GroundedAdCopyPlatform = 'meta' | 'linkedin' | 'google_rsa';
export type GroundedAdCopyOfferClass = 'commerce' | 'service' | 'authority' | 'unclear';

export interface GroundedAdCopyReference {
  readonly advertiser: string;
  readonly primaryText?: string;
  readonly headline?: string;
  readonly description?: string;
}

type GroundedCustomerQuote = GroundingBundle['customerQuotes'][number];

/**
 * Immutable evidence selected and authorized by the caller before generation.
 *
 * This module deliberately cannot discover a workspace, load a default brand,
 * resolve a product, or persist an answer. A route/tool must bind those facts
 * first and pass the exact snapshot that is allowed to influence this run.
 */
export interface GroundedAdCopyEvidence {
  readonly bundle: Readonly<GroundingBundle>;
  readonly claims: readonly AdClaim[];
  readonly customerQuotes: readonly GroundedCustomerQuote[];
  readonly selectedCatalogProduct: boolean;
}

export interface GenerateGroundedAdCopyInput {
  readonly product: string;
  readonly adPlatform: GroundedAdCopyPlatform;
  readonly offerClass: GroundedAdCopyOfferClass;
  readonly direction?: string;
  /**
   * The Concept's own hook, when the copy executes a Concept. It seeds the
   * lead: the ad leads with the specific the Concept's evidence carries
   * instead of replacing it with a broader line. Context only, never evidence.
   */
  readonly conceptHook?: string;
  /**
   * The Concept's own ask (`project_tasks.cta`). The offer sentence states it as
   * what the brand offers, in its own words; the button still carries the ask.
   */
  readonly conceptCta?: string;
  readonly reference?: GroundedAdCopyReference;
  /**
   * What the reader actually sees on the creative this copy will run beside —
   * its visible text and subject, in the labeller's own words. Copy is
   * written FROM the finished creative, not in parallel with it
   * (`METHOD-how-they-write-copy.md`: "the creative is upstream of the
   * copy"), so when a caller has this, it belongs here, not folded silently
   * into `direction`. Optional because most callers today have no creative
   * description to hand — its absence is a real gap, not a fabricated one.
   */
  readonly creativeDescription?: string;
  /**
   * ASSIGN a hook framework to each Meta primary text, instead of leaving
   * the model free to pick "exactly one" for both — the fix for finding (5),
   * 2026-09-03: three suggestion candidates that were three rerolls of the
   * same angle even though seven frameworks were on the menu. Index 0 pairs
   * with `copy.primaryTexts[0]` (the punchy shape), index 1 with
   * `primaryTexts[1]` (the fuller shape). Meta-only — LinkedIn/Google RSA
   * copy has no punchy/fuller primary-text pair to assign a framework to.
   * Optional: most callers still let the model choose, which is not a
   * regression, just the pre-existing behavior.
   */
  readonly frameworkIds?: readonly [HookFrameworkId, HookFrameworkId];
  readonly evidence: GroundedAdCopyEvidence;
  /** The already-resolved model for first-draft generation. */
  readonly model: LanguageModel;
  readonly abortSignal?: AbortSignal;
  /**
   * The only lines the quality judge may treat as evidence. An offer passes its own name and stated
   * facts here, so the judge never reads the brand kit's description as if it were ad text and a
   * warning can quote only a line that is in the ad. Absent, the judge gets the citable evidence.
   */
  readonly judgeEvidence?: readonly string[];
  /**
   * Epoch ms this call's caller will abort by (`write-paid-copy.ts`'s own
   * `PER_CONCEPT_TIMEOUT_MS` deadline, passed through unchanged). Optional —
   * a caller with no outer deadline (the chat `generate_ad_copy` tool, which
   * has its own 150s tool-level timeout instead) simply gets no degrade
   * behaviour, same as before this field existed.
   *
   * WHY THIS EXISTS. `produceMetaCopy`/`produceLinkedInCopy`/
   * `produceGoogleRsaCopy` call `safeGenerateObject`, which only swallows
   * `NoObjectGeneratedError`/`NoOutputGeneratedError` — an abort fired mid-
   * call is NOT one of those and propagates up uncaught, all the way to the
   * caller's own catch block, as a hard `outcome: 'failed'`. That is the
   * confirmed root cause of run `226b6173-…`'s 3-for-3 LinkedIn failure
   * (`brief_run_steps.detail.perConcept[].reason: 'This operation was
   * aborted'`, all three within ms of `PER_CONCEPT_TIMEOUT_MS`): the
   * judge-retry and quality-doctrine-repair work this module added
   * (664236c2c) can spend most of the outer budget on review/regeneration
   * passes that DO degrade safely on their own, leaving too little for the
   * generation call already in flight when the outer signal fires.
   *
   * `reviewWithRetry`'s own retry and `regenerate`'s rewrite/doctrine-regen
   * calls both check the remaining time against this deadline BEFORE
   * spending it, and skip themselves (degrading to `judge_incomplete` / a
   * reported-but-unrepaired finding — paths this module already has) rather
   * than start a call they cannot let finish. This never makes the deadline
   * itself larger; it only stops lower-priority work from starving whatever
   * generation call is already running when time is short.
   */
  readonly budgetDeadlineAt?: number;
  /**
   * A person is waiting on this call (the campaign drafter, the builder's
   * "Draft this"). Trades thinking time for latency and nothing else: the
   * drafts, the repairs and the judge think at 'low' instead of the model's
   * default, repairs run on `model` (the drafting tier) rather than a second
   * Pro-tier hop, and a regeneration needs less headroom before it starts.
   * Every gate still runs unchanged, on the same candidates, in the same order.
   * Measured 2026-09-30 on a real ITC LinkedIn brief: 231-317s with default
   * thinking on Pro, because each of ~7 sequential calls took 15-80s.
   */
  readonly interactive?: boolean;
  /**
   * The fast-tier model that WRITES and repairs the copy for an `interactive`
   * call. Omitted, drafting and repair use `model`. The deterministic gate and
   * the creative judge never use it.
   */
  readonly draftModel?: LanguageModel;
  /**
   * The title of the source page this ad was ordered from, when there is one
   * (the Brief pipeline's `anchored.title`). Lets the doctrine pass refuse a
   * hook that is the article's own headline pasted in as copy.
   */
  readonly sourceTitle?: string | null;
}

export interface GenerateGroundedAdCopyControl {
  /**
   * Optional caller-owned cancellation fence, checked after each first-draft
   * model call. It preserves lease semantics without importing the lease or
   * tool runtime into this engine.
   */
  readonly shouldDiscard?: () => boolean;
}

const AdCopyWarningSchema = z.object({
  field: z.string(),
  detail: z.string(),
  provenance: z.string(),
});

export const GroundedMetaAdCopySchema = z.object({
  offerClass: z.enum(['commerce', 'service', 'authority', 'unclear']),
  primaryTexts: z.tuple([
    z.object({ text: z.string(), shape: z.enum(['fuller', 'punchy']) }),
    z.object({ text: z.string(), shape: z.enum(['fuller', 'punchy']) }),
  ]),
  // Either order: `leadHeadlineRoleForFramework` (`ad-copy-doctrine.ts`) lets
  // the assigned hook framework decide which role ships as `headlines[0]`,
  // so the schema constrains each slot to the two known roles rather than
  // pinning position — one of each role, in either order.
  headlines: z
    .tuple([
      z.object({ text: z.string(), role: z.enum(['pain_restatement', 'outcome']) }),
      z.object({ text: z.string(), role: z.enum(['pain_restatement', 'outcome']) }),
    ])
    .refine(([first, second]) => first.role !== second.role, {
      message: 'Headlines must include exactly one pain_restatement and one outcome headline.',
    }),
  description: z.string(),
  cta: z.string(),
  evidence: z.array(z.string()),
  warnings: z.array(AdCopyWarningSchema),
});

export const GroundedLinkedInAdCopySchema = z.object({
  offerClass: z.enum(['commerce', 'service', 'authority', 'unclear']),
  commentary: z.string(),
  headline: z.string(),
  description: z.string().optional(),
  evidence: z.array(z.string()),
  warnings: z.array(AdCopyWarningSchema),
});

export const GroundedGoogleRsaAdCopySchema = z.object({
  offerClass: z.enum(['commerce', 'service', 'authority', 'unclear']),
  headlines: z.array(z.object({ text: z.string(), role: z.string() })),
  descriptions: z.array(z.object({ text: z.string(), role: z.string() })),
  paths: z.array(z.string()),
  evidence: z.array(z.string()),
  warnings: z.array(AdCopyWarningSchema),
});

export type GroundedMetaAdCopy = z.infer<typeof GroundedMetaAdCopySchema>;
export type GroundedLinkedInAdCopy = z.infer<typeof GroundedLinkedInAdCopySchema>;
export type GroundedGoogleRsaAdCopy = z.infer<typeof GroundedGoogleRsaAdCopySchema>;

export interface GroundedAdCopyWarning {
  readonly field: string;
  readonly detail: string;
  readonly provenance: string;
  /** Present on deterministic contract advisories; absent on creative-review notes. */
  readonly fixable?: boolean;
}

/**
 * One timed step of a `generateGroundedAdCopy` call, so a slow run can say what
 * consumed its budget. `step` is `producer`, `judge`, `regenerate` or `total`;
 * a regeneration contains its own producer call(s), so the steps overlap by
 * design and are never summed.
 */
export interface GroundedAdCopyTiming {
  readonly step: string;
  readonly ms: number;
  readonly outcome?: string;
}

export type GroundedAdCopyReady =
  | {
      readonly kind: 'ready';
      readonly adPlatform: 'meta';
      readonly copy: GroundedMetaAdCopy;
      readonly persisted: Record<string, unknown>;
      readonly offerClass: string;
      readonly warnings: GroundedAdCopyWarning[];
      readonly timings?: readonly GroundedAdCopyTiming[];
      /**
       * True when this candidate shipped with an unresolved quality issue —
       * a corny-gate finding still present after the bounded regenerate, or
       * the creative-review judge scoring it below bar (or failing to run at
       * all) even after its one rewrite attempt. NEVER means "blocked" —
       * `kind` stays `'ready'` either way — it means a human should look
       * before this runs, stated plainly instead of silently shipping a
       * clean-looking warning list. See `warnings` (field `'needs_review'`)
       * for the reasons.
       */
      readonly needsReview: boolean;
      /** The judge's structured read of the shipped copy. Null when the judge did not complete; the `needs_review` warning string is unchanged beside it. */
      readonly review?: StructuredAdReview | null;
      /** Parallel to `copy.primaryTexts` — the framework assigned to each, or `null` when the model chose freely (`frameworkIds` not supplied). */
      readonly frameworks?: readonly (HookFrameworkId | null)[];
    }
  | {
      readonly kind: 'ready';
      readonly adPlatform: 'linkedin';
      readonly copy: GroundedLinkedInAdCopy;
      readonly persisted: Record<string, unknown>;
      readonly offerClass: string;
      readonly warnings: GroundedAdCopyWarning[];
      readonly timings?: readonly GroundedAdCopyTiming[];
      readonly needsReview: boolean;
      readonly review?: StructuredAdReview | null;
    }
  | {
      readonly kind: 'ready';
      readonly adPlatform: 'google_rsa';
      readonly copy: GroundedGoogleRsaAdCopy;
      readonly persisted: Record<string, unknown>;
      readonly offerClass: string;
      readonly warnings: GroundedAdCopyWarning[];
      readonly timings?: readonly GroundedAdCopyTiming[];
      readonly needsReview: boolean;
      readonly review?: StructuredAdReview | null;
    };

export type GroundedAdCopyOutcome =
  | GroundedAdCopyReady
  | { readonly kind: 'discarded' }
  | {
      readonly kind: 'failure';
      readonly code: string;
      readonly message: string;
      readonly userError: string;
      /**
       * The gate's own sentences, one per finding, when a claims gate held the
       * copy back. `userError` opens on a generic "kept making a claim" line; a
       * caller that shows one sentence (the agent trail does) leads with these.
       */
      readonly reasons?: readonly string[];
    };

/**
 * Module-private: what a `produce*Copy` function actually returns while a
 * candidate is still travelling through the anti-corny gate and the judge's
 * retry. Identical to `GroundedAdCopyReady` except it carries its advisory
 * warnings UNCORRECTED and a `runAdvisoryRepair` closure the caller invokes
 * at most once, after the candidate has settled — see
 * `repairFixableAdvisories`'s doc comment for why. Never returned across this
 * module's public boundary: `generateGroundedAdCopy` strips `runAdvisoryRepair`
 * before its final `return`.
 */
type ReadyBeforeAdvisoryRepair =
  | (Extract<GroundedAdCopyReady, { adPlatform: 'meta' }> & {
      readonly runAdvisoryRepair: () => Promise<
        Pick<
          Extract<GroundedAdCopyReady, { adPlatform: 'meta' }>,
          'copy' | 'persisted' | 'warnings'
        >
      >;
    })
  | (Extract<GroundedAdCopyReady, { adPlatform: 'linkedin' }> & {
      readonly runAdvisoryRepair: () => Promise<
        Pick<
          Extract<GroundedAdCopyReady, { adPlatform: 'linkedin' }>,
          'copy' | 'persisted' | 'warnings'
        >
      >;
    })
  | (Extract<GroundedAdCopyReady, { adPlatform: 'google_rsa' }> & {
      readonly runAdvisoryRepair: () => Promise<
        Pick<
          Extract<GroundedAdCopyReady, { adPlatform: 'google_rsa' }>,
          'copy' | 'persisted' | 'warnings'
        >
      >;
    });

type ProducerOutcome =
  | ReadyBeforeAdvisoryRepair
  | { readonly kind: 'discarded' }
  | Extract<GroundedAdCopyOutcome, { kind: 'failure' }>;

interface PromptInput {
  readonly product: string;
  readonly adPlatform: GroundedAdCopyPlatform;
  readonly offerClass: GroundedAdCopyOfferClass;
  readonly direction?: string;
  readonly conceptHook?: string;
  readonly conceptCta?: string;
  readonly reference?: GroundedAdCopyReference;
  readonly creativeDescription?: string;
  readonly frameworkIds?: readonly [HookFrameworkId, HookFrameworkId];
}

/**
 * Everything upstream of the structural rules is identical on every platform:
 * what may be cited, what may not, and the fact that the request itself is
 * never evidence. Only the shape of the finished ad differs.
 */
function buildGroundedPreamble(
  opening: string,
  input: PromptInput,
  claims: readonly AdClaim[],
  customerQuotes: readonly GroundedCustomerQuote[],
  bundle: Readonly<GroundingBundle>,
) {
  // A client result about another industry is not offered at all when the Concept's
  // subject is known: the model cannot fill a slot with proof that is not about it.
  const citable = citableAdClaims([...claims]).filter(
    (claim) => !isResultTile(claim.text) || isTileRelevantTo(claim.text, input.conceptHook),
  );
  const lines: string[] = [opening, '', `Product/offer: ${input.product}`];
  if (input.creativeDescription?.trim()) {
    lines.push(
      '',
      'THE CREATIVE — this is where the ad starts. Read it before anything else below:',
      input.creativeDescription.trim(),
      "The image already chose the lane; your job is the strongest copy inside it. The first sentence of every primary text opens CONGRUENT with that image — true of the person in it, in the reader's own nouns — and never by describing what is in the frame or restating a promise the picture and its on-image text have already made. Repeat none of its on-image text verbatim, and never write a line that would read the same beside a different picture.",
    );
  } else {
    lines.push(
      '',
      'NO creative description was supplied, so you cannot write from the image. Say nothing about what any picture shows. Open on the most specific fact on file, or on a situation the evidence itself states. Do not invent a scene about the reader.',
    );
  }
  if (input.direction) lines.push(`Direction from the user: ${input.direction}`);
  if (input.conceptHook?.trim()) {
    lines.push(
      `THE CONCEPT'S HOOK (already approved as the idea this ad executes): "${input.conceptHook.trim()}"`,
      "Lead the ad's main text and shape its headline with the specific this hook carries (its figure, place or named mechanism). Do not replace it with a broader promise. If the hook states a client result, state it as the brand's own report of that client, in the words of the evidence line, because a result stated as a general fact is refused. Do not write a headline or an opening line that a competitor could run unchanged.",
    );
  }

  if (input.conceptCta?.trim()) {
    lines.push(
      `THE CONCEPT'S OWN CTA (the ask this Concept was approved with): "${input.conceptCta.trim()}"`,
      'The offer sentence that closes the main text states this offer in its own words, as what the brand offers ("ITC Tax offers a confidential, commitment-free second opinion"), never as an instruction to the reader. Do not swap it for a generic offer such as "a call" or "a consultation".',
    );
  }

  const reference = input.reference;
  if (reference) {
    const quoted: string[] = [];
    if (reference.primaryText) quoted.push(`- Their body copy: "${reference.primaryText.trim()}"`);
    if (reference.headline) quoted.push(`- Their headline: "${reference.headline.trim()}"`);
    if (reference.description)
      quoted.push(`- Their description: "${reference.description.trim()}"`);

    if (quoted.length > 0) {
      lines.push(
        '',
        '<reference-ad>',
        `The user chose an ad by ${reference.advertiser} as a REFERENCE FOR THE ANGLE ONLY. Here it is, quoted exactly as the provider reported it:`,
        ...quoted,
        '',
        'Read it for its ANGLE — the problem it leads with, who it is speaking to, what it offers, and how it closes. Then write something ORIGINAL for the product above.',
        'Do NOT reuse, rewrite, paraphrase, translate or lightly edit any phrase from it. If a line you are about to write would survive a find-and-replace of the brand name from their ad, it is too close — write a different line.',
        'If the product above cannot honestly support the same angle, write to an angle it CAN support and say nothing about theirs. Never bend the product to their pitch.',
        'This ad is not evidence about anything. It carries no proof it performed: the public ad archive reports no spend, impressions or engagement, and how long it has run is an observation about the advertiser, not a result. Nothing from it may enter the `evidence` field.',
        '</reference-ad>',
      );
    }
  }

  lines.push('');
  lines.push(
    'The product/offer line and direction above are CONTEXT ONLY — they tell you what to write about. They are the request, not evidence, and must never appear in the `evidence` field. Where the direction asks you to explain how something works or what it causes, write that only if a line in the evidence states it; otherwise state what the evidence says and stop.',
  );
  lines.push('');
  if (citable.length > 0) {
    lines.push('What you may cite as evidence (verbatim only, never paraphrase or invent):');
    for (const claim of citable) lines.push(`- ${claim.text}`);
  } else {
    lines.push(
      'NO sourced brand or product claims are on file. You have nothing citable to ground factual claims in — write only from the product/offer line above, keep every claim generic, and do not fabricate a source for it.',
    );
  }
  if (citable.some((claim) => claim.source === 'selected_catalog_product')) {
    lines.push(
      'Selected-product rule: quote or closely restate only the product facts above. Do not turn “control” or “power” into player outcomes, precision promises, match results, accuracy, stability, or testimonials unless those exact facts are present in the selected catalog evidence.',
    );
  }
  if (citable.some((claim) => claim.source === 'seller_fact')) {
    lines.push(
      'Seller-fact rule: lines that begin "Stated by the seller, not verified." are what the seller says about this product. Quote or closely restate them as the seller\'s own statement, never as verified, tested or guaranteed. They add no figure, comparison or outcome beyond their words.',
    );
  }
  if (customerQuotes.length > 0) {
    lines.push('Real customer quotes you may use verbatim:');
    for (const quote of customerQuotes.slice(0, 5)) lines.push(`- "${quote.quote}"`);
  } else {
    lines.push(
      'NO customer quotes are on file. Do not write in a customer\'s voice, do not invent a testimonial, do not claim social proof ("thousands of customers", "loved by", star ratings, etc).',
    );
  }
  lines.push(EVIDENCE_IS_FACT_NOT_PHRASING);
  lines.push(
    'Never join two facts from different evidence lines with "caused by", "due to", "driven by" or "because" unless a single evidence line states that cause. Two facts stay two statements.',
  );
  lines.push('');
  lines.push('CLAIMS — what this copy may assert, checked after you write:');
  lines.push(...PAID_CLAIMS_RULES.map((rule) => `- ${rule}`));

  const brandVoice = buildBrandVoiceBlock(bundle.brand);
  if (brandVoice.length > 0) {
    lines.push('');
    lines.push(...brandVoice);
  }

  const voiceRules = renderVoiceRules(bundle.brand.tone);
  if (voiceRules.length > 0) {
    lines.push('');
    lines.push(
      'VOICE, AS SENTENCE RULES:',
      'The brand stores its voice as adjectives. These are what those adjectives mean in a finished line, and each one is checkable in the copy you hand back.',
    );
    lines.push(...voiceRules);
  }

  const audienceBlock = buildAudienceBlock(bundle.audiences);
  if (audienceBlock.length > 0) {
    lines.push('');
    lines.push(...audienceBlock);
    if (input.creativeDescription?.trim()) {
      lines.push(
        'The creative decides which one. Pick the saved audience the person in that image could plausibly be. If none of them fits what is on screen, write to the person the image shows and say nothing about the saved rows — a row that describes someone reviewing a workflow rather than living a life is a test fixture, not a reader.',
      );
    }
  }

  const angleBlock = buildAngleBlock(bundle.angleBank);
  if (angleBlock.length > 0) {
    lines.push('');
    lines.push(...angleBlock);
  }

  lines.push('');
  lines.push('THE BRIEF — how this ad gets written. Work through it in this order:');
  lines.push(...COPY_BRIEF_RULES.map((rule, index) => `${index + 1}. ${rule}`));
  lines.push('');
  lines.push('CRAFT — how good copy is built:');
  lines.push(...SHARED_CRAFT_RULES.map((rule) => `- ${rule}`));
  lines.push('');
  if (input.frameworkIds) {
    const [punchyFrameworkId, fullerFrameworkId] = input.frameworkIds;
    lines.push(
      'Hook framework — ASSIGNED below, not a free choice. The framework decides the SHAPE of the argument: which sentence comes first, what the second sentence has to do, and where the claim lands. Each primary text commits fully to its own and blends in no other.',
      `- Primary text 1 (the punchy shape) uses: ${HOOK_FRAMEWORK_DESCRIPTIONS[punchyFrameworkId]}`,
      `  Worked example, a completely unrelated brand — copy the SHAPE, never a phrase: ${HOOK_FRAMEWORK_WORKED_EXAMPLES[punchyFrameworkId]}`,
      `- Primary text 2 (the fuller shape) uses: ${HOOK_FRAMEWORK_DESCRIPTIONS[fullerFrameworkId]}`,
      `  Worked example, a completely unrelated brand — copy the SHAPE, never a phrase: ${HOOK_FRAMEWORK_WORKED_EXAMPLES[fullerFrameworkId]}`,
    );
  } else {
    lines.push(...HOOK_FRAMEWORKS);
    lines.push(
      'Worked examples of each shape, all for unrelated brands — copy the SHAPE, never a phrase:',
    );
    for (const [id, example] of Object.entries(HOOK_FRAMEWORK_WORKED_EXAMPLES)) {
      lines.push(`- ${id}: ${example}`);
    }
  }
  lines.push('');
  lines.push(...buildSwapTestBlock(bundle.brand));
  lines.push('');
  lines.push(...HOOK_ANTI_PATTERNS.map((rule) => `- ${rule}`));
  lines.push('');
  lines.push('ANTI-CORNY — countable failure modes, checked after you write:');
  lines.push(...ANTI_CORNY_RULES.map((rule) => `- ${rule}`));
  lines.push(
    'House-banned words, in every field: magic, autopilot, autonomous, AI-powered, winner, and any guaranteed OUTCOME ("guaranteed results", "guaranteed growth"). An offer-shaped guarantee is fine and welcome — a refund window, a return policy, a delivery date — because that is a term of sale the advertiser can honour. Where an evidence line above uses a banned word, restate the fact in plain words instead: name the action the product takes and the point at which a person approves it.',
  );

  return lines;
}

export function adPlatformReviewLabel(adPlatform: 'meta' | 'google_rsa' | 'linkedin'): string {
  return adPlatform === 'google_rsa'
    ? 'Google Responsive Search Ad (RSA)'
    : adPlatform === 'linkedin'
      ? 'LinkedIn Sponsored Content'
      : 'Meta feed (Facebook/Instagram)';
}

export function renderCopyForReview(
  produced: Pick<GroundedAdCopyReady, 'adPlatform' | 'copy'>,
): string {
  const copy = produced.copy as {
    primaryTexts?: Array<{ text: string; shape: string }>;
    headlines?: Array<{ text: string; role: string }>;
    descriptions?: Array<{ text: string; role: string }>;
    paths?: string[];
    commentary?: string;
    headline?: string;
    description?: string;
    cta?: string;
  };

  if (produced.adPlatform === 'google_rsa') {
    return [
      'Headlines:',
      ...(copy.headlines ?? []).map(
        (headline) => `- [${headline.role}] ${headline.text} (${headline.text.length} chars)`,
      ),
      'Descriptions:',
      ...(copy.descriptions ?? []).map(
        (description) =>
          `- [${description.role}] ${description.text} (${description.text.length} chars)`,
      ),
      copy.paths?.length ? `Display paths: ${copy.paths.join(' / ')}` : 'Display paths: none',
    ].join('\n');
  }

  if (produced.adPlatform === 'linkedin') {
    return [
      `Intro text: ${copy.commentary ?? ''}`,
      `Headline: ${copy.headline ?? ''}`,
      copy.description ? `Description: ${copy.description}` : null,
    ]
      .filter(Boolean)
      .join('\n');
  }

  return [
    ...(copy.primaryTexts ?? []).map(
      (primary, index) => `Primary text ${index + 1} (${primary.shape}): ${primary.text}`,
    ),
    ...(copy.headlines ?? []).map(
      (headline, index) => `Headline ${index + 1} (${headline.role}): ${headline.text}`,
    ),
    `Description: ${copy.description ?? ''}`,
    `Call to action: ${copy.cta ?? ''}`,
  ].join('\n');
}

const EVIDENCE_CLOSING =
  'The `evidence` field must contain ONLY verbatim strings copied from the "What you may cite as evidence" or "Real customer quotes" lists above — never the product/offer line, never the direction, never anything you did not copy from those two lists. A canonical destination line proves only where the click lands; it does not prove product, price, performance, or availability claims. If you cannot ground a claim in one of those lines, cut the claim instead of inventing a source for it. Any evidence string that is not a verbatim match from those lists will be discarded before anyone sees it.';

const SHARED_WRITING_RULES = [
  '- Use the specifics on file. When the evidence carries a price, a material, a count, a rating or a date, the ad must use at least one of them instead of a general statement.',
  '- One promise per ad. Multiple value props is a failure — pick the single strongest one.',
  '- No em dashes. No AI-tell phrases ("elevated", "unleash", "discover the power of", "crafted for"). No invented stats, reviews, or ratings.',
  '- No personal-attribute call-outs ("you\'re overweight/in debt/depressed") — frame the condition, never assert it about the reader.',
  '- No cure/treat/prevent claims, and no guaranteed outcome. A guaranteed refund window or return policy is fine.',
];

/**
 * Every number this tag follows is read straight from
 * `paid_ad_delivered_metrics_v1` — provider-reported, and specifically NOT corroborated against
 * store revenue (no `attribution_settings` binding exists — see `ITERATE_AD_EVIDENCE_NOTE`,
 * `src/lib/paid-media/iterate-ad-eligibility.ts`). Attached to EVERY number in `buildPriorAdBlock`'s
 * output, immediately after it, never as a trailing summary line a model can drift away from — the
 * guard test (`generate-grounded-ad-copy.test.ts`) asserts exactly this placement.
 */
const PRIOR_AD_PROVIDER_TAG = '(provider-reported — not corroborated against store revenue)';

/**
 * A DIFFERENT evidence class from the tag above, on purpose: `detectableLiftInWindow` is Mora's own
 * arithmetic (`assessExperiment`, `src/lib/analytics/experiment-power.ts`), never a number the
 * provider reported. Sharing one tag between "what Meta/Google said happened" and "what Mora's own
 * power calculator computed" would be exactly the "evidence classes on one visual treatment" failure
 * this skill's doctrine exists to prevent (Triple Whale / Northbeam lesson, `CLAUDE.md`).
 */
const PRIOR_AD_MORA_COMPUTED_TAG = '(Mora-computed estimate, not a number the provider reported)';

/**
 * Fixed, number-free prose per `iterate-ad-diagnosis.ts` branch — deliberately NOT the branch's own
 * `reason` string, which quotes the account's actual CTR/comparison figures inline with no per-number
 * evidence tag of its own (that string is written for a human reading a receipt, not for a prompt
 * this module's own "every number tagged" guard has to hold to). Restating the CONCLUSION in words,
 * with the real numbers already tagged and listed separately above this line in the prompt, keeps the
 * guard simple and honest rather than trying to retrofit tags onto prose that was never built for it.
 */
const PRIOR_AD_DIAGNOSIS_SENTENCE: Record<IterationDiagnosisBranch, string> = {
  new_concept:
    'Diagnosis: Mora reads this as a weak hook or offer. The first move is a genuinely NEW concept — a different angle, not a variation of this one.',
  landing_page_not_ad:
    'Diagnosis: Mora reads the bottleneck as the destination this ad points to, not the ad copy itself — new copy alone would not be expected to move the numbers above. (This branch should never reach a copy prompt; if you are reading this sentence in a live prompt, the caller failed to honor the refusal.)',
  insufficient_signal:
    'Diagnosis: Mora could not localize a specific weakness from the numbers above. Treat everything here as a starting hypothesis, not a settled conclusion.',
  leave_alone:
    'Diagnosis: Mora reads this ad as converting and not meaningfully behind comparison. Any new copy here is a genuinely ADDITIONAL test, never a replacement for something already working.',
};

function formatPriorAdCount(value: number | null): string {
  if (value === null) return 'not yet measured';
  return `${value} ${PRIOR_AD_PROVIDER_TAG}`;
}

function formatPriorAdSpend(minorUnits: number | null): string {
  if (minorUnits === null) return 'not yet measured';
  return `$${(minorUnits / 100).toFixed(2)} ${PRIOR_AD_PROVIDER_TAG}`;
}

function buildPriorAdPowerSentence(power: PriorAdGround['powerVerdict']): string[] {
  const lift =
    power.detectableLiftInWindow !== null
      ? `Mora's own estimate: within the stated window, the smallest relative lift Mora could reliably detect is ${(power.detectableLiftInWindow * 100).toFixed(0)}% ${PRIOR_AD_MORA_COMPUTED_TAG}.`
      : 'Mora could not compute a detectable-lift estimate for this ad — treat the numbers above as directional only, not as proof either way.';
  return [
    `Statistical read: ${power.verdict}. ${lift}`,
    ...power.limitations.map((limitation) => `Limitation: ${limitation}`),
  ];
}

/**
 * PLAN-iterate-this-ad-2026-09-03.md Unit 5. Presents a delivered prior ad as a HYPOTHESIS TO
 * REACT TO, never as ground truth about what to write next — the copy is quoted for reference only
 * (never evidence, same framing `<reference-ad>` already uses above for a competitor's ad), and
 * every performance number is immediately followed by its own evidence-class tag rather than a
 * trailing disclaimer the model can drift away from over a long prompt.
 *
 * `[]` (nothing rendered) for every ordinary generation request — this only fires when
 * `copy-suggestions-core.ts` resolved an eligible `iterateFromCommandId` and attached a
 * `PriorAdGround` to the bundle; see that module's `resolvePriorAdGround` for the refusal this
 * function's caller never even reaches when the prior ad is ineligible.
 */
export function buildPriorAdBlock(priorAd: GroundingBundle['priorAd']): string[] {
  if (!priorAd) return [];
  const lines: string[] = [
    'PRIOR AD — a delivered ad on this same product, offered as a HYPOTHESIS TO REACT TO, never as ground truth about what to write next. It is not evidence; nothing from it may enter the `evidence` field:',
    `Its copy, quoted exactly as it ran (not evidence, only for reference): "${priorAd.copyText}"`,
  ];
  if (priorAd.label) {
    const parts = [
      priorAd.label.theme ? `theme "${priorAd.label.theme}"` : null,
      priorAd.label.hookMechanism ? `hook mechanism "${priorAd.label.hookMechanism}"` : null,
      priorAd.label.desireOrientation
        ? `desire orientation "${priorAd.label.desireOrientation}"`
        : null,
    ].filter((part): part is string => part !== null);
    if (parts.length > 0) lines.push(`Its creative label: ${parts.join(', ')}.`);
  }
  lines.push(
    'PRIOR AD PERFORMANCE — every number below is evidence-classed immediately after it, and none of it is corroborated against store revenue:',
    `- Spend: ${formatPriorAdSpend(priorAd.metrics.spendMinorUnits)}.`,
    `- Impressions: ${formatPriorAdCount(priorAd.metrics.impressions)}.`,
    `- Clicks: ${formatPriorAdCount(priorAd.metrics.clicks)}.`,
    `- Conversions: ${formatPriorAdCount(priorAd.metrics.conversions)}.`,
    PRIOR_AD_DIAGNOSIS_SENTENCE[priorAd.diagnosis.branch],
    ...buildPriorAdPowerSentence(priorAd.powerVerdict),
    priorAd.evidenceNote,
    // Nit (2026-09-04 re-fix round): "say nothing about a number not listed above" read as
    // permission to state the numbers that ARE listed above (spend/impressions/clicks/conversions,
    // present in this block for the first time — no organic winner-metrics analog exists to have
    // set this precedent). Those are Mora's own analysis inputs, not claims for the ad copy to
    // make; the instruction must forbid all of them, not license the ones it named.
    "React to this as a hypothesis, not a script: write toward what the diagnosis above calls for, reuse none of this ad's exact wording, and never state any number from this block in the ad copy — not the metrics, not the diagnosis's own figures, none of it.",
  );
  return lines;
}

function buildMetaPrompt(
  input: PromptInput,
  claims: readonly AdClaim[],
  customerQuotes: readonly GroundedCustomerQuote[],
  bundle: Readonly<GroundingBundle>,
) {
  const lines = buildGroundedPreamble(
    'You are writing Meta (Facebook/Instagram) paid ad copy for one product. Follow this exactly:',
    input,
    claims,
    customerQuotes,
    bundle,
  );
  const priorAdBlock = buildPriorAdBlock(bundle.priorAd);
  if (priorAdBlock.length > 0) {
    lines.push('', ...priorAdBlock);
  }
  const learningsBlock = buildLearningsCopyContext(bundle.priorLearnings ?? []);
  if (learningsBlock.length > 0) {
    lines.push('', ...learningsBlock);
  }
  lines.push('');
  lines.push('META CRAFT:');
  lines.push(...META_CRAFT_RULES.map((rule) => `- ${rule}`));
  lines.push('');
  lines.push('Structural rules (hard requirements):');
  lines.push(
    '- Exactly 2 primary texts. One "fuller" (advertorial/story), one "punchy" (short hook-only). They must differ in shape, not just wording.',
  );
  lines.push(
    '- Exactly 2 headlines: one role "pain_restatement" (names the problem), one role "outcome" (names the transformation). Never two of the same role.',
  );
  lines.push(
    `- Exactly 1 description, ${COPY_BRIEF_LIMITS.descriptionMaxChars} characters or fewer. It carries a reason to click (an offer the evidence lists, the plain price as the evidence states it, a guarantee, a proof point). Write "now", "was", "off", "save" or "sale" only when the evidence lists a compare-at price; otherwise state the price plainly or leave it out. Never a feature label, never a targeting label, and never anything load-bearing.`,
  );
  lines.push('- One CTA, a short verb phrase. Never append the product name to it.');
  lines.push(
    "- The first sentence of each primary text names the product or offer and carries a detail the evidence states. It never opens on a general statement about the reader's situation, season or need.",
  );
  lines.push(
    `- The hook (first sentence of each primary text) is ${COPY_BRIEF_LIMITS.hookMaxWords} words or fewer, stands alone inside ~${COPY_BRIEF_LIMITS.hookFoldMaxChars} characters, and contains a concrete noun, an evidenced figure, or a named moment.`,
  );
  lines.push(
    `- Each headline is ${COPY_BRIEF_LIMITS.headlineMaxWords} words or fewer, contains a verb, and reads correctly with no primary text above it. It shares the primary text's product nouns but never restates the hook as a sentence: it supplies the object, the moment, or the outcome the hook left out. A bare noun phrase is a label, not a headline.`,
  );
  lines.push(
    `- For cold prospecting: the punchy primary text is ${COPY_BRIEF_LIMITS.punchyMaxWords} words or fewer, the fuller one ${COPY_BRIEF_LIMITS.fullerMinWords} to ${COPY_BRIEF_LIMITS.primaryTextMaxWords}. Count the words.`,
  );
  lines.push(
    '- Exactly one claim per primary text, with the specific thing that makes it true, said the way a person says it out loud. Do not build sentences as [instruction] because [fact].',
  );
  lines.push(...SHARED_WRITING_RULES);
  lines.push('- 7th-grade reading level.');
  lines.push('');
  lines.push(EVIDENCE_CLOSING);
  return lines.join('\n');
}

function buildGoogleRsaPrompt(
  input: PromptInput,
  claims: readonly AdClaim[],
  customerQuotes: readonly GroundedCustomerQuote[],
  bundle: Readonly<GroundingBundle>,
) {
  const spec = AD_SPECS.google_rsa;
  const lines = buildGroundedPreamble(
    'You are writing a Google Responsive Search Ad (RSA) asset inventory for one product or offer. Follow this exactly:',
    input,
    claims,
    customerQuotes,
    bundle,
  );
  const priorAdBlock = buildPriorAdBlock(bundle.priorAd);
  if (priorAdBlock.length > 0) {
    lines.push('', ...priorAdBlock);
  }
  const learningsBlock = buildLearningsCopyContext(bundle.priorLearnings ?? []);
  if (learningsBlock.length > 0) {
    lines.push('', ...learningsBlock);
  }
  lines.push('');
  lines.push('GOOGLE RSA CRAFT:');
  lines.push(...GOOGLE_RSA_CRAFT_RULES.map((rule) => `- ${rule}`));
  lines.push('');
  lines.push('Structural rules (hard requirements — Google rejects anything over these):');
  lines.push(
    `- Write ${spec.headlineCount.max} headlines. Each must be ${spec.headline.hardMax} characters or fewer, counted exactly, including spaces.`,
  );
  lines.push(
    '- Cover all five headline roles, roughly three each: "keyword_mirror" (the phrase someone would search), "outcome" (the result in the customer\'s words), "proof" (a specific number or fact from the evidence), "offer" (price, guarantee, shipping, or terms), "cta_brand" (the action or the brand\'s trust line).',
  );
  lines.push(
    `- Write ${spec.descriptionCount.max} descriptions, each ${spec.description.hardMax} characters or fewer, one per role: "benefit", "proof", "offer", "objection".`,
  );
  lines.push(
    `- Write up to ${spec.pathCount.max} display paths, each ${spec.path.hardMax} characters or fewer, readable words rather than keyword strings. Return an empty list if none fit naturally.`,
  );
  lines.push(
    '- No two headlines may say the same thing in different words. If you cannot find a genuinely distinct angle for a slot, write fewer headlines rather than padding with a synonym.',
  );
  lines.push(
    '- Only claim a price, discount, guarantee, or stock state if it appears verbatim in the evidence above. Google penalises copy that promises what the landing page does not show.',
  );
  lines.push(...SHARED_WRITING_RULES);
  lines.push('');
  lines.push(
    'You do NOT have this account\'s Google Ads keyword or search-term data. Write the "keyword_mirror" headlines from the natural language a buyer would type to find this product, and do not claim or imply that any of them reflect measured search volume.',
  );
  lines.push('');
  lines.push(EVIDENCE_CLOSING);
  return lines.join('\n');
}

function buildLinkedInPrompt(
  input: PromptInput,
  claims: readonly AdClaim[],
  customerQuotes: readonly GroundedCustomerQuote[],
  bundle: Readonly<GroundingBundle>,
) {
  const spec = AD_SPECS.linkedin;
  const lines = buildGroundedPreamble(
    'You are writing LinkedIn Sponsored Content ad copy for one product or offer. Follow this exactly:',
    input,
    claims,
    customerQuotes,
    bundle,
  );
  const learningsBlock = buildLearningsCopyContext(bundle.priorLearnings ?? []);
  if (learningsBlock.length > 0) {
    lines.push('', ...learningsBlock);
  }
  lines.push('');
  lines.push('LINKEDIN CRAFT:');
  lines.push(...LINKEDIN_CRAFT_RULES.map((rule) => `- ${rule}`));
  lines.push('');
  lines.push('Structural rules (hard requirements):');
  lines.push(
    `- One "commentary": the intro text above the image, in three short paragraphs separated by a blank line, ${LINKEDIN_COMMENTARY_TARGET_MIN}-${LINKEDIN_COMMENTARY_EXPANDED_MAX} characters in all. LinkedIn shows only the first ~${LINKEDIN_HOOK_FOLD_CHARS} characters before "see more" — that fold IS the ad for most impressions — and the reader who taps it reads the rest.`,
  );
  lines.push(
    `  1. HOOK (the first ~${LINKEDIN_HOOK_FOLD_CHARS} characters, one or two sentences): state ONE specific, sourced fact from the evidence above — a figure, or the article's or brand's own claim, verbatim or near-verbatim — plus, if it fits, the one item in that fact the reader owns. Not a rhetorical question, not scene-setting, not an observation a competitor in the same vertical could open with unchanged. This is checked after you write, deterministically, against the evidence: a hook with no evidence fact in it is a shipped defect, not a style choice.`,
  );
  // Demo QA 2026-11-03: three of four first LinkedIn drafts were refused for a
  // lead that sounded brand-specific and was not in the evidence ("averages 18
  // months of pre-sale planning", "cutoff around 16 December"). Meta and Google
  // are not held to the lead bar (`findGenericAdLead` is LinkedIn-only), so the
  // same evidence passed there. The gate stays; the drafter is told exactly what
  // the gate accepts, and given the evidence's own specifics to copy from.
  const leadSpecifics = statedLeadSpecifics(
    citableAdClaims([...claims]).map((claim) => claim.text),
  );
  lines.push(
    `  The hook's specific is COPIED from one evidence line, never composed: a figure written exactly as that line writes it, a place that line names, or a run of at least four consecutive words from it. A figure, date, duration, count or price that no evidence line states is refused even when it sounds like this brand's own, and so is a brand name on its own or a paraphrase of the evidence in your words.${leadSpecifics.length > 0 ? ` Specifics the evidence states, any one of which may open the hook or the headline: ${leadSpecifics.join(', ')}.` : ' The evidence states no figure or named place, so open on a run of its own words.'}`,
  );
  lines.push(
    "  2. BODY (one or two sentences): why that fact matters to the specific asset, decision or exposure this reader owns — a concrete consequence the evidence supports, in the reader's own terms. State what is true and what is at stake; never the legal, tax or regulatory conclusion (that they qualify, are exempt, will save a given amount). Anything the evidence does not say stays out.",
  );
  lines.push(
    '  3. CLOSE (one sentence): the named, low-friction next step the evidence says this brand actually offers (a review, an assessment, a consultation, a call — whatever the evidence names), written as a STATEMENT of what the brand offers ("ITC Tax offers a ..."), never as an instruction to the reader ("Request ...", "Book ...", "Contact ...", "Get ...": the button carries the ask, and a written instruction is refused), never as an outcome the reader will receive. Add the brand\'s own sourced proof point only if one is listed above that is about THIS ad\'s subject (the same asset, industry or service as the hook), and then only as the CLAIMS rules above require: attributed to the brand, scoped exactly as the evidence scopes it, caveated, once. Never fill the slot with a result about a different industry or asset; no proof point is better than an unrelated one. If the lead already states a result, the close does not state a second one. If the evidence names no such offer, close on the brand\'s own sourced positioning claim; never invent an offer. This is not the CTA button text — never write "Learn more", "Click here" or "Contact us".',
  );
  lines.push(
    '  Structure only — the bracketed parts are filled from THIS ad\'s evidence, and nothing from this skeleton is a fact you may use: "[Source or brand] [specific sourced fact]. [The one item in it the reader owns.] / [Why that matters to the asset or decision the reader holds, one concrete consequence.] / [Brand] offers [named low-commitment offer from the evidence]. [What it starts with, if the evidence says.]"',
  );
  lines.push(
    `- One "headline": the bold line under the image. Under ${spec.headline.adviseMax} characters. It must say what the thing IS or DOES — on LinkedIn the headline is read as a label, not as a teaser. Build it from the evidence's own nouns (the asset, the place, the service), so it could only be this brand's. It never repeats the opening sentence of the commentary, and what counts as specific is exactly one of: a figure the evidence states, a named place, an abbreviation the evidence uses, or a client kind with the scope the results line gives it ("multi-state data center developer"). A client result figure may lead the headline only framed as that one client's ("$10M property tax saved for a Texas energy client"), never promised to the reader ("save $10M"); the attributed sentence still goes in the commentary.`,
  );
  lines.push(
    `- One "description": short supporting line under the headline, under ${spec.description.adviseMax} characters. Optional reinforcement only, never load-bearing.`,
  );
  lines.push(
    '- Write for a working professional evaluating this during their workday. Plain, concrete, specific. No hype, no growth-hacker voice, no "🚀", no engagement bait ("Agree?", "Thoughts?").',
  );
  lines.push(
    '- Do NOT write a CTA line into the copy. LinkedIn renders the call to action as its own button chosen from a fixed list, so a written "Learn more" duplicates it. Some evidence lines are the page\'s own button or instruction text ("Request a ... review.", "Upload your documents"); they are not facts to copy into the intro, and no sentence of the intro or the description may open on an instruction to the reader.',
  );
  lines.push(
    '- The description is a new line, not the intro again: it must not restate the facts the intro already states.',
  );
  lines.push(...SHARED_WRITING_RULES);
  lines.push(
    '- Industry terms are fine where they are the accurate word; never reach for jargon to sound credible.',
  );
  lines.push('');
  lines.push(EVIDENCE_CLOSING);
  return lines.join('\n');
}

/**
 * Generate, normalize, repair, contract-check, and creatively review one
 * already-grounded paid-ad copy request.
 *
 * There are intentionally no account reads, tool runtime lookups, command
 * transitions, or writes in this module. Callers own authorization and
 * persistence; this engine owns the single canonical prompt and quality path.
 */
/**
 * Floors for `budgetDeadlineAt`'s degrade checks — see that field's doc
 * comment. Both are deliberately smaller than the calls they gate
 * (`reviewAdCopy`'s own 25s ceiling, a full generation pass) because they
 * exist to skip a call that CANNOT plausibly finish, not to reserve slack
 * for it: `reviewAdCopy`/`repairAdCopy` already degrade to `null` safely on
 * their own timeout or on the outer abort, so a generous floor here would
 * only turn a safe, self-terminating call into a skipped one for no reason.
 */
const MIN_REMAINING_FOR_JUDGE_RETRY_MS = 10_000;
/** A regeneration is a full, uncaught generation call (see the field's doc
 *  comment) — its floor is real headroom, not a "might as well try" one. */
const MIN_REMAINING_FOR_REGENERATION_MS = 20_000;
/** Same floor for an `interactive` call, whose drafting tier finishes a pass in ~4s, not ~40s. */
const MIN_REMAINING_FOR_INTERACTIVE_REGENERATION_MS = 8_000;
/**
 * Own ceiling for each producer and judge model call, combined with the caller's `abortSignal`. One
 * stalled call used to hold the whole run until the caller's budget; capped, it fails fast so the
 * existing retry (or an offer's composed floor) still fits inside the offer budget.
 */
export const PER_MODEL_CALL_TIMEOUT_MS = 20_000;
/** The longest a non-interactive call may take when its caller sets no tighter budget. */
export const NON_INTERACTIVE_CALL_CAP_MS = 60_000;

const LOW_THINKING_PROVIDER_OPTIONS = {
  google: { thinkingConfig: { thinkingLevel: 'low' as const } },
};

/** A results tile's "(x3)" must not reach the prompt through a hook, a direction or a claim. */
function withoutTileMultipliers(request: GenerateGroundedAdCopyInput): GenerateGroundedAdCopyInput {
  const hasMultiplier = (text: string | undefined): boolean =>
    Boolean(text && /\(\s*[x×]\s*\d+\s*\)/i.test(text));
  if (
    !hasMultiplier(request.conceptHook) &&
    !hasMultiplier(request.direction) &&
    !request.evidence.claims.some((claim) => hasMultiplier(claim.text))
  ) {
    return request;
  }
  return {
    ...request,
    ...(request.conceptHook ? { conceptHook: stripTileMultiplier(request.conceptHook) } : {}),
    ...(request.direction ? { direction: stripTileMultiplier(request.direction) } : {}),
    evidence: {
      ...request.evidence,
      claims: request.evidence.claims.map((claim) => ({
        ...claim,
        text: stripTileMultiplier(claim.text),
      })),
    },
  };
}

/**
 * Said when a rewrite was skipped because the time left was too short. True for
 * both callers (the plan drafter's per-leg budget and the brief-order
 * per-Concept timeout), so it names neither.
 */
export const REWRITE_OUT_OF_TIME_SENTENCE =
  'Mora ran out of time to rewrite it. Edit it, or save and rewrite later.';

export async function generateGroundedAdCopy(
  rawRequest: GenerateGroundedAdCopyInput,
  control: GenerateGroundedAdCopyControl = {},
): Promise<GroundedAdCopyOutcome> {
  const request = withoutTileMultipliers(rawRequest);
  const engineStartedAt = Date.now();
  const timings: GroundedAdCopyTiming[] = [];
  const timed = async <T>(
    step: string,
    run: () => Promise<T>,
    outcomeOf?: (value: T) => string,
  ): Promise<T> => {
    const startedAt = Date.now();
    let outcome = 'threw';
    try {
      const value = await run();
      outcome = outcomeOf ? outcomeOf(value) : 'ok';
      return value;
    } finally {
      timings.push({ step, ms: Math.max(0, Date.now() - startedAt), outcome });
    }
  };
  const { abortSignal, budgetDeadlineAt } = request;
  const callSignal = (): AbortSignal =>
    abortSignal
      ? AbortSignal.any([abortSignal, AbortSignal.timeout(PER_MODEL_CALL_TIMEOUT_MS)])
      : AbortSignal.timeout(PER_MODEL_CALL_TIMEOUT_MS);
  /** A producer call that hits its own cap degrades like an unusable result; the caller's abort still propagates. */
  const generateCapped: typeof safeGenerateObject = async (options) => {
    const cap = AbortSignal.timeout(callCapMs());
    const signal = options.abortSignal ? AbortSignal.any([options.abortSignal, cap]) : cap;
    try {
      return await safeGenerateObject({ ...options, abortSignal: signal });
    } catch (error) {
      if (cap.aborted && !options.abortSignal?.aborted) {
        return { object: options.fallback, usedFallback: true };
      }
      throw error;
    }
  };
  const interactive = request.interactive === true;
  const model = interactive ? (request.draftModel ?? request.model) : request.model;
  const draftProviderOptions = interactive ? LOW_THINKING_PROVIDER_OPTIONS : undefined;
  const repairModel = interactive ? model : undefined;
  const regenerationFloorMs = interactive
    ? MIN_REMAINING_FOR_INTERACTIVE_REGENERATION_MS
    : MIN_REMAINING_FOR_REGENERATION_MS;
  const remainingBudgetMs = (): number =>
    budgetDeadlineAt === undefined ? Number.POSITIVE_INFINITY : budgetDeadlineAt - Date.now();
  // The 20s cap fits the interactive draft model at low thinking. A non-interactive caller drafts
  // with the full model, which takes longer than 20s; its own budget bounds the call instead.
  const callCapMs = (): number =>
    interactive
      ? PER_MODEL_CALL_TIMEOUT_MS
      : Math.max(
          PER_MODEL_CALL_TIMEOUT_MS,
          Math.min(remainingBudgetMs(), NON_INTERACTIVE_CALL_CAP_MS),
        );
  const { bundle, customerQuotes, selectedCatalogProduct } = request.evidence;
  const claims = [...request.evidence.claims];
  const citable = citableAdClaims(claims);
  const promptInput: PromptInput = {
    product: request.product,
    adPlatform: request.adPlatform,
    offerClass: request.offerClass,
    ...(request.direction ? { direction: request.direction } : {}),
    ...(request.conceptHook ? { conceptHook: request.conceptHook } : {}),
    ...(request.conceptCta ? { conceptCta: request.conceptCta } : {}),
    ...(request.reference ? { reference: request.reference } : {}),
    ...(request.creativeDescription ? { creativeDescription: request.creativeDescription } : {}),
    ...(request.frameworkIds ? { frameworkIds: request.frameworkIds } : {}),
  };

  const isLinkedIn = request.adPlatform === 'linkedin';
  const isGoogleRsa = request.adPlatform === 'google_rsa';
  const basePrompt = isGoogleRsa
    ? buildGoogleRsaPrompt(promptInput, claims, customerQuotes, bundle)
    : isLinkedIn
      ? buildLinkedInPrompt(promptInput, claims, customerQuotes, bundle)
      : buildMetaPrompt(promptInput, claims, customerQuotes, bundle);

  const promptWithNotes = (notes: readonly string[]) =>
    notes.length === 0
      ? basePrompt
      : [
          basePrompt,
          '',
          '<revision>',
          'A creative director reviewed your previous attempt and sent it back. Fix exactly these problems, keeping every rule above intact:',
          ...notes.map((note, index) => `${index + 1}. ${note}`),
          'Do not weaken the evidence rules to make the copy sharper. If a stronger claim is not supported by the evidence above, find a sharper way to say something that IS supported.',
          '</revision>',
        ].join('\n');
  const system = isGoogleRsa
    ? 'You write Google Responsive Search Ad assets. You never fabricate evidence, reviews, statistics, or claims not present in the material you were given, and you count characters exactly because Google rejects assets over its limits.'
    : isLinkedIn
      ? 'You write LinkedIn Sponsored Content ad copy for a professional audience. You never fabricate evidence, reviews, statistics, or claims not present in the material you were given.'
      : 'You write direct-response Meta ad copy. You never fabricate evidence, reviews, statistics, or claims not present in the material you were given.';

  const verifiableSources = [
    ...citable.map((claim) => claim.text),
    ...customerQuotes.map((quote) => quote.quote),
  ];
  /**
   * Everything a figure in the finished copy could legitimately have come
   * from: the citable claims, the customer quotes, and the three
   * caller-supplied context strings. Wider than `verifiableSources` on
   * purpose — a number the user typed into the direction is sourced even
   * though it is not citable evidence.
   */
  const evidenceCorpus = [
    ...verifiableSources,
    request.product,
    request.direction ?? '',
    request.creativeDescription ?? '',
  ].join('\n');
  const contractOptions = {
    allowCustomerQuotes: customerQuotes.length > 0,
    enforceSelectedProductEvidence: selectedCatalogProduct,
    referenceTexts: [
      request.reference?.primaryText,
      request.reference?.headline,
      request.reference?.description,
    ].filter((text): text is string => Boolean(text && text.trim())),
  };

  /**
   * BOUNDED to one call per `generateGroundedAdCopy` invocation, deliberately.
   *
   * Before 2026-09-03 each `produce*Copy` function called this internally, at
   * the end of every one of its own invocations — including the ones the
   * anti-corny gate and the judge's rewrite trigger via `regenerate`. A
   * headline that stayed over Meta's advisory length across every retry
   * therefore paid for `repairAdCopy` up to 3 times for one shipped ad
   * (`generate-ad-copy-advisory-repair.test.ts`, red 3/5 before this fix).
   * The repair itself is cheap to skip (it's a no-op when nothing is
   * `fixable`) but never cheap to run, so multiplying it by every regenerate
   * pass repeats the exact "individually-defensible bounds multiply" failure
   * `MAX_COPY_REGENERATIONS`'s own comment already fixed once for the gate vs.
   * the judge. Each `produce*Copy` now returns its candidate with advisory
   * warnings computed but UNCORRECTED (`args.advise` only), plus a
   * `runAdvisoryRepair` closure over this same function; the caller invokes
   * that closure exactly once, after `runAntiCornyGate` and the judge retry
   * have both settled on the candidate that will actually ship.
   */
  async function repairFixableAdvisories<TDraft, TOutput extends { evidence: string[] }>(args: {
    output: TOutput;
    draft: unknown;
    prompt: string;
    advise: (output: TOutput) => AdContractWarning[];
    check: (
      output: TOutput,
      options: AdContractOptions,
    ) => Array<{ field: string; detail: string }>;
    normalize: (draft: TDraft) => { output: TOutput | null; issues: unknown[] };
    schema?: z.ZodType<TDraft>;
    /**
     * The anti-corny/banned-language/em-dash gate, when this platform has
     * one (Meta only — `gateNotes` below). A repair is a rewrite, and a
     * rewrite is exactly as capable of reintroducing a gate failure (an em
     * dash, house-banned language, an unevidenced claim) as the first draft
     * was — the same reasoning `runAntiCornyGate`'s own comment states for
     * re-gating the judge's rewrite. Before 2026-09-03 this repair pass was
     * the LAST mutation of the shipped copy and nothing downstream re-ran
     * the gate against it: `checkAdContract` alone is a STRUCTURAL check
     * (shape, length, evidence-backing) and cannot see an em dash or
     * "guaranteed" in a headline.
     *
     * Compared against the PRE-repair candidate, not judged in isolation:
     * `args.output` can already be carrying a `warn`-severity gate finding
     * unrelated to what the repair was asked to fix (a primary text with no
     * stated mechanism, say, while only the headline's length gets
     * repaired) — that finding is not new, it is already accounted for in
     * `gateReasons` downstream, and discarding an otherwise-good repair
     * over it would silently disable the repair pass for any draft that
     * happens to carry an unrelated pre-existing warning. Only a finding
     * the repair itself INTRODUCED — present after, absent before — sinks
     * the repair, exactly like `check`'s "discards a repair that introduces
     * a new [contract] violation" already does for structural violations.
     */
    gateCheck?: (output: TOutput) => ReadonlyArray<{ regenerate: boolean; detail: string }>;
  }): Promise<{ output: TOutput; warnings: AdContractWarning[] }> {
    const warnings = args.advise(args.output);
    const fixable = warnings.filter((warning) => warning.fixable);
    if (fixable.length === 0) {
      return { output: args.output, warnings };
    }

    const repaired = await repairAdCopy<TDraft>({
      model: repairModel,
      draft: args.draft,
      issues: fixable.map((warning) => `${warning.field}: ${warning.detail}`),
      system,
      prompt: args.prompt,
      abortSignal: callSignal(),
      schema: args.schema,
    });
    if (!repaired) return { output: args.output, warnings };

    const normalized = args.normalize(repaired);
    if (!normalized.output) return { output: args.output, warnings };

    const repairedOutput = normalized.output;
    repairedOutput.evidence = filterVerifiableEvidence(repairedOutput.evidence, verifiableSources);

    if (args.check(repairedOutput, contractOptions).length > 0) {
      return { output: args.output, warnings };
    }
    if (args.gateCheck) {
      const preRepairGateFindings = new Set(
        args
          .gateCheck(args.output)
          .filter((note) => note.regenerate)
          .map((note) => note.detail),
      );
      const introducedByRepair = args
        .gateCheck(repairedOutput)
        .filter((note) => note.regenerate && !preRepairGateFindings.has(note.detail));
      if (introducedByRepair.length > 0) {
        return { output: args.output, warnings };
      }
    }

    return { output: repairedOutput, warnings: args.advise(repairedOutput) };
  }

  const placeholderFallback = {
    offerClass: 'unclear' as const,
    primaryTexts: [
      { text: '', shape: 'punchy' as const },
      { text: '', shape: 'fuller' as const },
    ],
    headlines: [
      { text: '', role: 'pain_restatement' as const },
      { text: '', role: 'outcome' as const },
    ],
    description: '',
    cta: '',
    evidence: ['placeholder — never rendered, see usedFallback check'],
    reasoning: 'placeholder — never rendered, see usedFallback check',
  };

  const googleRsaPlaceholderFallback = {
    offerClass: 'unclear' as const,
    headlines: [
      { text: 'placeholder', role: 'outcome' as const },
      { text: 'placeholder two', role: 'proof' as const },
      { text: 'placeholder three', role: 'offer' as const },
    ],
    descriptions: [
      { text: 'placeholder description', role: 'benefit' as const },
      { text: 'placeholder description two', role: 'proof' as const },
    ],
    paths: [],
    evidence: ['placeholder — never rendered, see usedFallback check'],
    reasoning: 'placeholder — never rendered, see usedFallback check',
  };

  const linkedInPlaceholderFallback = {
    offerClass: 'unclear' as const,
    commentary: '',
    headline: '',
    description: '',
    evidence: ['placeholder — never rendered, see usedFallback check'],
    reasoning: 'placeholder — never rendered, see usedFallback check',
  };

  const produceMetaCopy = async (notes: readonly string[]): Promise<ProducerOutcome> => {
    const prompt = promptWithNotes(notes);
    const result = await generateCapped({
      model,
      schema: MetaAdCopyDraftSchema,
      prompt,
      system,
      abortSignal,
      providerOptions: draftProviderOptions,
      fallback: placeholderFallback,
    });

    if (control.shouldDiscard?.()) return { kind: 'discarded' };

    if (result.usedFallback) {
      return {
        kind: 'failure',
        code: 'generation_failed',
        message: 'The inner model returned no usable ad copy.',
        userError: 'Ad copy generation failed and returned no usable draft. Please retry.',
      };
    }

    // HEADLINE ROLE BY FRAMEWORK: `frameworkIds[0]` is the punchy primary
    // text's framework — the one representing this ad's overall angle when a
    // framework was assigned at all — and decides which role ships as
    // `headlines[0]`. See `leadHeadlineRoleForFramework`.
    const leadHeadlineRole = leadHeadlineRoleForFramework(request.frameworkIds?.[0]);
    const normalizeMeta = (draft: MetaAdCopyDraft) =>
      normalizeMetaAdCopy(draft, { leadHeadlineRole });

    let normalized = normalizeMeta(result.object);

    if (!normalized.output) {
      const repaired = await repairAdCopy({
        model: repairModel,
        draft: result.object,
        issues: normalized.issues.map((issue) => `${issue.field}: ${issue.detail}`),
        system,
        prompt,
        abortSignal: callSignal(),
      });
      normalized = repaired ? normalizeMeta(repaired) : { output: null, issues: normalized.issues };
    }

    if (!normalized.output) {
      return {
        kind: 'failure',
        code: 'invalid_output',
        message: 'The generated ad copy could not satisfy the required shape.',
        userError: `Could not produce a structurally valid ad set: ${normalized.issues.map((issue) => issue.detail).join('; ')}`,
      };
    }

    let output: MetaAdCopyOutput = normalized.output;
    output.evidence = filterVerifiableEvidence(output.evidence, verifiableSources);

    let violations = checkAdContract(output, contractOptions);
    if (violations.length > 0) {
      const repaired = await repairAdCopy({
        model: repairModel,
        draft: result.object,
        issues: violations.map((violation) => `${violation.field}: ${violation.detail}`),
        system,
        prompt,
        abortSignal: callSignal(),
      });
      const repairedNormalized = repaired ? normalizeMeta(repaired) : null;
      if (!repairedNormalized?.output) {
        return {
          kind: 'failure',
          code: 'unsupported_claims',
          message: 'Generated copy could not be repaired from evidence.',
          userError:
            'Ad copy included unsupported claims and could not be repaired from the selected evidence. Please retry with a narrower direction.',
        };
      }
      output = repairedNormalized.output;
      output.evidence = filterVerifiableEvidence(output.evidence, verifiableSources);
      violations = checkAdContract(output, contractOptions);
    }
    if (violations.length > 0) {
      return {
        kind: 'failure',
        code: 'ad_contract_failed',
        message: 'Generated copy did not satisfy the ad contract.',
        userError: `Ad copy failed the contract: ${violations.map((violation) => violation.detail).join('; ')}`,
      };
    }

    // UNCORRECTED advisory warnings — no repair attempt here. See
    // `repairFixableAdvisories`'s doc comment: the one repair pass for this
    // candidate runs later, only if this candidate is still the one shipping
    // after the anti-corny gate and the judge have both settled.
    const warnings = adviseAdContract(output);
    const buildReady = (finalOutput: MetaAdCopyOutput, finalWarnings: AdContractWarning[]) => {
      const { reasoning: _reasoning, ...publicOutput } = finalOutput;
      const finalCopy: GroundedMetaAdCopy = { ...publicOutput, warnings: finalWarnings };
      return {
        copy: finalCopy,
        warnings: finalWarnings,
        persisted: {
          primaryTexts: finalCopy.primaryTexts,
          headlines: finalCopy.headlines,
          description: finalCopy.description,
          cta: finalCopy.cta,
          evidence: finalCopy.evidence,
        },
      };
    };
    const runAdvisoryRepair = async () => {
      const repaired = await repairFixableAdvisories<MetaAdCopyDraft, MetaAdCopyOutput>({
        output,
        draft: result.object,
        prompt,
        advise: adviseAdContract,
        check: checkAdContract,
        normalize: normalizeMeta,
        // Re-run the SAME anti-corny/banned-language/em-dash gate the first
        // draft and the judge's rewrite both had to clear — see
        // `repairFixableAdvisories`'s `gateCheck` doc. `gateNotes` reads
        // `GroundedMetaAdCopy`'s shape (primaryTexts/headlines/description/
        // cta), all present on the repaired `MetaAdCopyOutput`; `warnings`
        // is irrelevant to every check the gate runs, so `[]` is a safe
        // stand-in rather than a real value.
        gateCheck: (candidate) => gateNotes({ ...candidate, warnings: [] }),
      });
      return buildReady(repaired.output, repaired.warnings);
    };
    const ready = buildReady(output, warnings);
    return {
      kind: 'ready',
      adPlatform: 'meta',
      ...ready,
      offerClass: ready.copy.offerClass,
      needsReview: false,
      ...(request.frameworkIds ? { frameworks: request.frameworkIds } : {}),
      runAdvisoryRepair,
    };
  };

  const produceGoogleRsaCopy = async (notes: readonly string[]): Promise<ProducerOutcome> => {
    const prompt = promptWithNotes(notes);
    const result = await generateCapped({
      model,
      schema: GoogleRsaAdCopyDraftSchema,
      prompt,
      system,
      abortSignal,
      providerOptions: draftProviderOptions,
      fallback: googleRsaPlaceholderFallback,
    });

    if (control.shouldDiscard?.()) return { kind: 'discarded' };

    if (result.usedFallback) {
      return {
        kind: 'failure',
        code: 'generation_failed',
        message: 'The inner model returned no usable ad copy.',
        userError: 'Ad copy generation failed and returned no usable draft. Please retry.',
      };
    }

    let normalized = normalizeGoogleRsaAdCopy(result.object);

    if (!normalized.output) {
      const repaired = await repairAdCopy({
        model: repairModel,
        draft: result.object,
        issues: normalized.issues.map((issue) => `${issue.field}: ${issue.detail}`),
        system,
        prompt,
        abortSignal: callSignal(),
        schema: GoogleRsaAdCopyDraftSchema,
      });
      normalized = repaired
        ? normalizeGoogleRsaAdCopy(repaired)
        : { output: null, issues: normalized.issues };
    }

    if (!normalized.output) {
      return {
        kind: 'failure',
        code: 'invalid_output',
        message: 'The generated ad copy could not satisfy the required shape.',
        userError: `Could not produce a structurally valid ad set: ${normalized.issues.map((issue) => issue.detail).join('; ')}`,
      };
    }

    let output: GoogleRsaAdCopyOutput = normalized.output;
    output.evidence = filterVerifiableEvidence(output.evidence, verifiableSources);

    let violations = checkGoogleRsaAdContract(output, contractOptions);
    if (violations.length > 0) {
      const repaired = await repairAdCopy({
        model: repairModel,
        draft: result.object,
        issues: violations.map((violation) => `${violation.field}: ${violation.detail}`),
        system,
        prompt,
        abortSignal: callSignal(),
        schema: GoogleRsaAdCopyDraftSchema,
      });
      const repairedNormalized = repaired ? normalizeGoogleRsaAdCopy(repaired) : null;
      if (!repairedNormalized?.output) {
        return {
          kind: 'failure',
          code: 'unsupported_claims',
          message: 'Generated copy could not be repaired from evidence.',
          userError:
            'Ad copy included unsupported claims and could not be repaired from the selected evidence. Please retry with a narrower direction.',
        };
      }
      output = repairedNormalized.output;
      output.evidence = filterVerifiableEvidence(output.evidence, verifiableSources);
      violations = checkGoogleRsaAdContract(output, contractOptions);
    }
    if (violations.length > 0) {
      return {
        kind: 'failure',
        code: 'ad_contract_failed',
        message: 'Generated copy did not satisfy the ad contract.',
        userError: `Ad copy failed the contract: ${violations.map((violation) => violation.detail).join('; ')}`,
      };
    }

    // UNCORRECTED advisory warnings — see the Meta producer's identical
    // comment above. The one repair pass for this candidate is deferred to
    // `runAdvisoryRepair`, called at most once, only on the candidate that
    // survives to ship.
    const warnings = adviseGoogleRsaAdContract(output);
    const buildReady = (finalOutput: GoogleRsaAdCopyOutput, finalWarnings: AdContractWarning[]) => {
      const { reasoning: _reasoning, ...publicOutput } = finalOutput;
      const finalCopy: GroundedGoogleRsaAdCopy = { ...publicOutput, warnings: finalWarnings };
      return {
        copy: finalCopy,
        warnings: finalWarnings,
        persisted: {
          headlines: finalCopy.headlines,
          descriptions: finalCopy.descriptions,
          paths: finalCopy.paths,
          evidence: finalCopy.evidence,
        },
      };
    };
    const runAdvisoryRepair = async () => {
      const repaired = await repairFixableAdvisories<GoogleRsaAdCopyDraft, GoogleRsaAdCopyOutput>({
        output,
        draft: result.object,
        prompt,
        advise: adviseGoogleRsaAdContract,
        check: checkGoogleRsaAdContract,
        normalize: normalizeGoogleRsaAdCopy,
        schema: GoogleRsaAdCopyDraftSchema,
      });
      return buildReady(repaired.output, repaired.warnings);
    };
    const ready = buildReady(output, warnings);
    return {
      kind: 'ready',
      adPlatform: 'google_rsa',
      ...ready,
      offerClass: ready.copy.offerClass,
      needsReview: false,
      runAdvisoryRepair,
    };
  };

  const produceLinkedInCopy = async (notes: readonly string[]): Promise<ProducerOutcome> => {
    const prompt = promptWithNotes(notes);
    const result = await generateCapped({
      model,
      schema: LinkedInAdCopyDraftSchema,
      prompt,
      system,
      abortSignal,
      providerOptions: draftProviderOptions,
      fallback: linkedInPlaceholderFallback,
    });

    if (control.shouldDiscard?.()) return { kind: 'discarded' };

    if (result.usedFallback) {
      return {
        kind: 'failure',
        code: 'generation_failed',
        message: 'The inner model returned no usable ad copy.',
        userError: 'Ad copy generation failed and returned no usable draft. Please retry.',
      };
    }

    let normalized = normalizeLinkedInAdCopy(result.object);

    if (!normalized.output) {
      const repaired = await repairAdCopy({
        model: repairModel,
        draft: result.object,
        issues: normalized.issues.map((issue) => `${issue.field}: ${issue.detail}`),
        system,
        prompt,
        abortSignal: callSignal(),
        schema: LinkedInAdCopyDraftSchema,
      });
      normalized = repaired
        ? normalizeLinkedInAdCopy(repaired)
        : { output: null, issues: normalized.issues };
    }

    if (!normalized.output) {
      return {
        kind: 'failure',
        code: 'invalid_output',
        message: 'The generated ad copy could not satisfy the required shape.',
        userError: `Could not produce a structurally valid ad set: ${normalized.issues.map((issue) => issue.detail).join('; ')}`,
      };
    }

    let output: LinkedInAdCopyOutput = normalized.output;
    output.evidence = filterVerifiableEvidence(output.evidence, verifiableSources);

    let violations = checkLinkedInAdContract(output, contractOptions);
    if (violations.length > 0) {
      const repaired = await repairAdCopy({
        model: repairModel,
        draft: result.object,
        issues: violations.map((violation) => `${violation.field}: ${violation.detail}`),
        system,
        prompt,
        abortSignal: callSignal(),
        schema: LinkedInAdCopyDraftSchema,
      });
      const repairedNormalized = repaired ? normalizeLinkedInAdCopy(repaired) : null;
      if (!repairedNormalized?.output) {
        return {
          kind: 'failure',
          code: 'unsupported_claims',
          message: 'Generated copy could not be repaired from evidence.',
          userError:
            'Ad copy included unsupported claims and could not be repaired from the selected evidence. Please retry with a narrower direction.',
        };
      }
      output = repairedNormalized.output;
      output.evidence = filterVerifiableEvidence(output.evidence, verifiableSources);
      violations = checkLinkedInAdContract(output, contractOptions);
    }
    if (violations.length > 0) {
      return {
        kind: 'failure',
        code: 'ad_contract_failed',
        message: 'Generated copy did not satisfy the ad contract.',
        userError: `Ad copy failed the contract: ${violations.map((violation) => violation.detail).join('; ')}`,
      };
    }

    // UNCORRECTED advisory warnings — see the Meta producer's identical
    // comment above. The one repair pass for this candidate is deferred to
    // `runAdvisoryRepair`, called at most once, only on the candidate that
    // survives to ship.
    const warnings = adviseLinkedInWithExpandedFold(output);
    const buildReady = (finalOutput: LinkedInAdCopyOutput, finalWarnings: AdContractWarning[]) => {
      const {
        reasoning: _reasoning,
        destinationUrl: _destinationUrl,
        ...publicOutput
      } = finalOutput;
      const finalCopy: GroundedLinkedInAdCopy = { ...publicOutput, warnings: finalWarnings };
      return {
        copy: finalCopy,
        warnings: finalWarnings,
        persisted: {
          commentary: finalCopy.commentary,
          headline: finalCopy.headline,
          ...(finalCopy.description ? { description: finalCopy.description } : {}),
          evidence: finalCopy.evidence,
        },
      };
    };
    const runAdvisoryRepair = async () => {
      const repaired = await repairFixableAdvisories<LinkedInAdCopyDraft, LinkedInAdCopyOutput>({
        output,
        draft: result.object,
        prompt,
        advise: adviseLinkedInWithExpandedFold,
        check: checkLinkedInAdContract,
        normalize: normalizeLinkedInAdCopy,
        schema: LinkedInAdCopyDraftSchema,
      });
      return buildReady(repaired.output, repaired.warnings);
    };
    const ready = buildReady(output, warnings);
    return {
      kind: 'ready',
      adPlatform: 'linkedin',
      ...ready,
      offerClass: ready.copy.offerClass,
      needsReview: false,
      runAdvisoryRepair,
    };
  };

  const runProducer = (notes: readonly string[]) =>
    timed(
      'producer',
      () =>
        isGoogleRsa
          ? produceGoogleRsaCopy(notes)
          : isLinkedIn
            ? produceLinkedInCopy(notes)
            : produceMetaCopy(notes),
      (outcome) => outcome.kind,
    );

  let review: AdCopyReview | null = null;
  /**
   * Each field is 'needs_review' for a review that genuinely scored the copy
   * and found something, or 'judge_incomplete' for a review that never ran
   * at all (timeout or error, on BOTH the first attempt and its one retry) —
   * two different honest states a marketer reads differently: "look at
   * this, the judge flagged it" vs. "nobody has judged this yet". See
   * `reviewWithRetry`'s doc comment for the retry itself.
   */
  const judgeReasons: Array<{ field: 'needs_review' | 'judge_incomplete'; detail: string }> = [];
  let judgeUnsupported: readonly string[] = [];

  /**
   * ONE retry of the judge call itself — never a copy regeneration — on a
   * timeout or an error, because `reviewAdCopy` already returns `null` for
   * both. This is the judge's OWN timeout budget, independent of
   * `PER_CONCEPT_TIMEOUT_MS` (the whole-call abort a caller like
   * `write-paid-copy.ts` wraps this function in): each attempt gets
   * `reviewAdCopy`'s own internal 25s bound
   * (`AbortSignal.any([abortSignal, AbortSignal.timeout(25_000)])`), so two
   * attempts cost at most ~50s of the outer budget, never the generation
   * budget itself. A review that is still null after the retry is reported
   * as genuinely unjudged, never silently dropped and never faked as a pass.
   */
  const reviewWithRetry = async (renderedCopy: string, platformLabel: string) => {
    const args = {
      renderedCopy,
      platformLabel,
      product: request.product,
      evidence: request.judgeEvidence ?? verifiableSources,
      abortSignal: callSignal(),
      ...(interactive ? { thinkingLevel: 'low' as const } : {}),
    };
    const judgeAttempt = () =>
      timed(
        'judge',
        () => reviewAdCopy(args),
        (value) => (value === null ? 'no_result' : 'scored'),
      );
    const first = await judgeAttempt();
    if (first !== null) return first;
    // Degrade to `judge_incomplete` (the caller already treats a null return
    // that way) rather than spend the retry's own ~25s when too little of
    // `budgetDeadlineAt` remains for it to plausibly matter — see
    // `MIN_REMAINING_FOR_JUDGE_RETRY_MS`'s doc comment. `reviewAdCopy` itself
    // is already abort-safe; this check exists only to stop it from being
    // STARTED when it cannot help, not because starting it would be unsafe.
    if (remainingBudgetMs() < MIN_REMAINING_FOR_JUDGE_RETRY_MS) return null;
    return judgeAttempt();
  };

  // ONE shared regeneration budget for the whole call (see
  // MAX_COPY_REGENERATIONS): the deterministic gate and the judge's rewrite
  // draw from the same pot, so their individually-defensible bounds can no
  // longer multiply into six frontier calls for one suggestion. Returns
  // `null` when the budget is spent, which every caller reports honestly
  // rather than treating as "nothing to fix".
  let regenerationsUsed = 0;
  // Set when `regenerate` bails on the TIME floor rather than the COUNT one,
  // so the two messages below can say which actually happened — "the shared
  // budget was already spent" is a different, false claim when the real
  // reason is "there was no time left in this Concept's own deadline".
  let regenerationSkippedForTime = false;
  const regenerate = async (notes: readonly string[]): Promise<ProducerOutcome | null> => {
    if (regenerationsUsed >= MAX_COPY_REGENERATIONS) return null;
    // A regeneration is a full, uncaught `safeGenerateObject` call (see
    // `budgetDeadlineAt`'s doc comment on `GenerateGroundedAdCopyInput`) — an
    // abort mid-call propagates as a hard failure, not a degrade, so this is
    // checked BEFORE starting one, never left to the outer signal to catch.
    if (remainingBudgetMs() < regenerationFloorMs) {
      regenerationSkippedForTime = true;
      return null;
    }
    regenerationsUsed += 1;
    return timed(
      'regenerate',
      () => runBestProducer(notes),
      (outcome) => outcome.kind,
    );
  };

  // ── THE DETERMINISTIC ANTI-CORNY GATE, INSIDE GENERATION ────────────────
  // Meta-only: the gate reads `copy.primaryTexts`/`headlines`, a shape only
  // the Meta draft has. Bounded to MAX_ANTI_CORNY_REGENERATIONS per pass and
  // to MAX_COPY_REGENERATIONS across the call — each pass is a real model
  // call, and "keep going until the gate is happy" is the exact unbounded-
  // spend failure `review-ad-copy.ts` already documents for the judge below.
  // A finding that survives the bound never ships silently: it is recomputed
  // against the shipped candidate below and becomes a `needs_review` reason.
  //
  // Applied to EVERY candidate that might ship — the first draft AND the
  // judge's rewrite — because the judge's rewrite replaces `produced`
  // wholesale and is exactly as capable of reintroducing a gate failure (an
  // em dash, an over-length description) as the first draft was.
  const gateNotes = (copy: GroundedMetaAdCopy): GenerationGateNote[] => [
    ...evaluateGenerationGate(copy, request.creativeDescription, verifiableSources),
    ...evaluateBannedLanguageGate(copy),
    ...evaluateEmDashGate(copy),
  ];

  const runAntiCornyGate = async (candidate: ProducerOutcome): Promise<ProducerOutcome> => {
    if (candidate.kind !== 'ready' || candidate.adPlatform !== 'meta') return candidate;
    let current = candidate;
    let gateFindings = gateNotes(current.copy).filter((note) => note.regenerate);
    let attempt = 0;
    while (gateFindings.length > 0 && attempt < MAX_ANTI_CORNY_REGENERATIONS) {
      // Positive constraints only — `finding.fix` is written as the next
      // move ("Rewrite around the outcome..."), never as a "don't do X" ban.
      // Negative prompts are a proven no-op on the Gemini models this repo
      // runs generation on; the fix field exists so this loop never needs a
      // negative one.
      const positiveNotes = [...new Set(gateFindings.map((finding) => finding.fix))];
      const retry = await regenerate(positiveNotes);
      if (retry === null) break;
      attempt++;
      if (retry.kind !== 'ready' || retry.adPlatform !== 'meta') break;
      current = retry;
      gateFindings = gateNotes(current.copy).filter((note) => note.regenerate);
    }
    return current;
  };

  // ── ONE SETTLING LOOP: deterministic doctrine + creative judge, one budget ──
  //
  // Doctrine findings (qualifier fidelity, scenario scope, outcome promises,
  // per-brand avoid-phrases, ungrounded legal/directive claims, the LinkedIn
  // hook-fact check) are cheap, deterministic and objective, so they run
  // FIRST on every candidate; the paid creative judge (a model call) only
  // runs on a candidate the deterministic checks have already cleared.
  // Every regeneration — whichever check ordered it — draws on the SAME
  // `MAX_COPY_REGENERATIONS` pool, and one regeneration carries the doctrine
  // fixes AND the judge's complaint together, so a rewrite for one defect
  // cannot silently reintroduce the other.
  //
  // Why one loop, not a judge phase followed by a doctrine phase (the
  // structure before 2026-09-25 round 3): the judge's verdict was recorded
  // against a candidate the doctrine phase could then REPLACE, so the reason
  // shipped on copy it had never scored; a regeneration that came back as a
  // `failure` (contract check failed) ended the doctrine phase on its FIRST
  // attempt ("detection without enforcement" — a real run shipped an
  // unqualified "Average 43% tax reduction" that way); and nothing
  // re-checked the judge's rewrite against the doctrine. Here a failed
  // regeneration keeps the previous candidate and tries again while budget
  // remains, and a replaced candidate is always re-checked and re-judged.
  const BRAND_OWN_SOURCES: ReadonlySet<AdClaim['source']> = new Set([
    'brand_profile',
    'product_catalog',
    'canonical_destination',
    'selected_catalog_product',
  ]);
  const PAGE_TEXT_SOURCES: ReadonlySet<AdClaim['source']> = new Set([
    'product_catalog',
    'selected_catalog_product',
    'canonical_destination',
  ]);
  const doctrineContext: DoctrineContext = {
    brandSources: citable
      .filter((claim) => BRAND_OWN_SOURCES.has(claim.source))
      .map((claim) => claim.text),
    otherSources: [
      ...citable.filter((claim) => !BRAND_OWN_SOURCES.has(claim.source)).map((claim) => claim.text),
      ...customerQuotes.map((quote) => quote.quote),
    ],
    pageSources: citable
      .filter((claim) => PAGE_TEXT_SOURCES.has(claim.source))
      .map((claim) => claim.text),
    articleSources: citable
      .filter((claim) => claim.source === 'brief_source')
      .map((claim) => claim.text),
    sourceTitle: request.sourceTitle ?? null,
    brandName: bundle.brand.brandName,
    productNames: bundle.productTitles,
    competitorNames: bundle.brand.competitors,
    conceptSubject: request.conceptHook ?? null,
    conceptEvidence: request.conceptHook?.trim()
      ? [
          request.conceptHook,
          request.direction ?? '',
          ...conceptOwnResultTiles(verifiableSources.filter(isResultTile), request.conceptHook),
          ...verifiableSources.filter((source) => isSoldOfferFact(source, request.product)),
        ]
      : [],
  };
  const evaluateDoctrine = (
    candidate: Extract<ProducerOutcome, { kind: 'ready' }>,
  ): QualityDoctrineFinding[] =>
    evaluateQualityDoctrine(
      shippingFields(candidate.adPlatform, candidate.copy),
      verifiableSources,
      request.direction,
      bundle.brand.avoidPhrases,
      doctrineContext,
    );

  /**
   * INTERACTIVE ONLY: write the pass twice at once on the fast tier and keep
   * the candidate the deterministic checks like better. Two concurrent calls
   * take as long as one, and a draft that trips a blocking claims rule (the
   * usual reason for another ~5s round) is far less likely from both. The
   * winner is picked by the same doctrine and gate the loop below applies and
   * is still checked, judged and gated in full afterwards. A pass counts once
   * against the shared regeneration budget.
   */
  function candidateBadness(outcome: ProducerOutcome): number {
    if (outcome.kind !== 'ready') return Number.POSITIVE_INFINITY;
    const doctrine = evaluateDoctrine(outcome);
    const blocking = doctrine.filter((finding) => finding.blocking).length;
    const gate =
      outcome.adPlatform === 'meta'
        ? gateNotes(outcome.copy).filter((note) => note.regenerate).length
        : 0;
    return blocking * 10 + (doctrine.length - blocking) + gate;
  }

  async function runBestProducer(notes: readonly string[]): Promise<ProducerOutcome> {
    if (!interactive) return runProducer(notes);
    const pair = await Promise.all([runProducer(notes), runProducer(notes)]);
    const discarded = pair.find((outcome) => outcome.kind === 'discarded');
    if (discarded) return discarded;
    return candidateBadness(pair[1]) < candidateBadness(pair[0]) ? pair[1] : pair[0];
  }

  let produced = await runAntiCornyGate(await runBestProducer([]));

  // A FIRST draft that fails its structural/contract checks used to end the
  // whole Concept on the spot (real run, 2026-09-25: "Account-wide customer
  // proof cannot be used for a selected catalog product…" after one draft and
  // one repair, 47s in, no copy at all) while the regeneration budget sat
  // unspent. The failure's own reason is the positive direction for the next
  // attempt; the same shared budget bounds it.
  while (produced.kind === 'failure') {
    const retry = await regenerate([
      `The previous attempt was rejected: ${produced.userError} Write it again so that does not happen.`,
    ]);
    if (retry === null) break;
    produced = await runAntiCornyGate(retry);
  }

  const platformLabelFor = (candidate: Extract<ProducerOutcome, { kind: 'ready' }>) =>
    adPlatformReviewLabel(candidate.adPlatform);

  // The judge scores the CURRENT candidate at most once; a replaced candidate
  // resets these so a verdict is never read against copy it did not score.
  let candidateJudged = false;
  let judgeUnavailable = false;
  let readyRewritesSinceJudgeFailed = 0;
  let failedRewriteAttempts = 0;
  // INTERACTIVE ONLY. The last candidate that cleared every deterministic
  // check before the judge ordered a rewrite. A rewrite can reintroduce a
  // claims defect that the time left cannot fix; the earlier candidate, which
  // passed the gate and carries the judge's verdict as a reason, is then what
  // ships, instead of a refusal of copy that was fine to review.
  let cleanBeforeRewrite: {
    candidate: Extract<ProducerOutcome, { kind: 'ready' }>;
    review: AdCopyReview | null;
    judgeUnavailable: boolean;
  } | null = null;
  let revertedToCleanCandidate = false;

  const judgeCandidate = (candidate: Extract<ProducerOutcome, { kind: 'ready' }>) =>
    reviewWithRetry(renderCopyForReview(candidate), platformLabelFor(candidate));

  while (produced.kind === 'ready') {
    const findings = evaluateDoctrine(produced);
    if (findings.length === 0 && !candidateJudged) {
      review = await judgeCandidate(produced);
      candidateJudged = true;
      judgeUnavailable = review === null;
    }

    const judgeFailed = candidateJudged && review !== null && !reviewPasses(review);
    if (findings.length === 0 && !judgeFailed) break;
    // A person is waiting: ONE judge-ordered rewrite, then ship what the
    // deterministic gate cleared, with the judge's verdict stated as a
    // `needs_review` reason below rather than spending another round on it.
    if (interactive && findings.length === 0 && readyRewritesSinceJudgeFailed >= 1) break;

    const notes = [
      ...new Set([
        ...findings.map((finding) => finding.fix),
        ...(judgeFailed && review ? judgeRewriteNotes(review) : []),
      ]),
    ];
    const retry = await regenerate(notes);
    if (retry === null) {
      break;
    }
    if (retry.kind === 'discarded') return retry;
    if (interactive && findings.length === 0 && judgeFailed) {
      cleanBeforeRewrite = { candidate: produced, review, judgeUnavailable };
    }
    if (retry.kind !== 'ready') {
      // The rewrite itself failed its contract/shape checks: keep the
      // candidate we have and try again while budget remains — a failed
      // attempt spends budget (`regenerate` counts it) but never ends the
      // loop on its own.
      failedRewriteAttempts++;
      continue;
    }
    if (judgeFailed) readyRewritesSinceJudgeFailed++;
    produced = await runAntiCornyGate(retry);
    candidateJudged = false;
    review = null;
    judgeUnavailable = false;
  }

  if (
    cleanBeforeRewrite !== null &&
    produced.kind === 'ready' &&
    evaluateDoctrine(produced).some((finding) => finding.blocking)
  ) {
    produced = cleanBeforeRewrite.candidate;
    review = cleanBeforeRewrite.review;
    judgeUnavailable = cleanBeforeRewrite.judgeUnavailable;
    candidateJudged = true;
    readyRewritesSinceJudgeFailed = 0;
    revertedToCleanCandidate = true;
  }

  // A candidate that settled with only NON-blocking findings left (or none)
  // and was never scored because the budget ran out first still gets one
  // judge pass, so its `needs_review` reasons include the score. A
  // candidate that still carries a blocking claims finding is about to be
  // refused below, and is not worth a judge call.
  if (
    produced.kind === 'ready' &&
    !candidateJudged &&
    !evaluateDoctrine(produced).some((finding) => finding.blocking)
  ) {
    review = await judgeCandidate(produced);
    candidateJudged = true;
    judgeUnavailable = review === null;
  }

  if (produced.kind === 'ready') {
    if (judgeUnavailable) {
      judgeReasons.push({
        field: 'judge_incomplete',
        detail:
          'Quality review: the automated creative-review judge did not complete after a retry (timed out or errored twice) — this copy has not been judged.',
      });
    } else if (review !== null && !reviewPasses(review)) {
      // Held, not reported yet: the claims gate below removes what it can
      // locate in a LinkedIn ad and only then reports what is left.
      judgeUnsupported = review.unsupportedAssertions ?? [];
    }
    if (
      review !== null &&
      !reviewPasses(review) &&
      (review.hookScore < AD_COPY_HOOK_SCORE_BAR || review.isGeneric)
    ) {
      const scoreLine = `scored ${review.hookScore}/10 on the hook`;
      const weakest = `Weakest line: "${review.weakestLine}".`;
      if (revertedToCleanCandidate) {
        judgeReasons.push({
          field: 'needs_review',
          detail: `Quality review: ${scoreLine}${review.isGeneric ? ' and reads as generic' : `, under the bar of ${AD_COPY_HOOK_SCORE_BAR}`}. The rewrite it asked for broke a claims rule that the time left could not fix, so this is the version from before the rewrite. ${weakest}`,
        });
      } else if (readyRewritesSinceJudgeFailed > 0) {
        judgeReasons.push({
          field: 'needs_review',
          detail: review.isGeneric
            ? `Quality review: ${scoreLine} and still reads as generic after a rewrite — a competitor could run this with only the product name swapped. ${weakest}`
            : `Quality review: ${scoreLine} after a rewrite, still under the bar of ${AD_COPY_HOOK_SCORE_BAR}. ${weakest}`,
        });
      } else if (failedRewriteAttempts > 0) {
        judgeReasons.push({
          field: 'needs_review',
          detail: `Quality review: ${scoreLine}${review.isGeneric ? ' and reads as generic' : `, under the bar of ${AD_COPY_HOOK_SCORE_BAR}`}, and the rewrite attempt failed to generate. ${weakest}`,
        });
      } else {
        const scoreSentence = `Quality review: ${scoreLine}${review.isGeneric ? ' and reads as generic' : `, under the bar of ${AD_COPY_HOOK_SCORE_BAR}`}`;
        judgeReasons.push({
          field: 'needs_review',
          detail: regenerationSkippedForTime
            ? `${scoreSentence}. ${REWRITE_OUT_OF_TIME_SENTENCE} ${weakest}`
            : `${scoreSentence}, and the shared regeneration budget of ${MAX_COPY_REGENERATIONS} was already spent fixing the deterministic gate, so no rewrite was attempted. ${weakest}`,
        });
      }
    }
  }

  if (produced.kind === 'failure' || produced.kind === 'discarded') return produced;

  // THE ONE ADVISORY-REPAIR PASS FOR THIS CALL, on the candidate that
  // actually settled — never on one the gate or the judge discarded. See
  // `repairFixableAdvisories`'s doc comment for the cost this replaces (up to
  // 3 repair calls for one shipped ad, `generate-ad-copy-advisory-repair.test.ts`).
  // The cast is safe, not a type escape: `runAdvisoryRepair` is bound inside
  // the same `produce*Copy` call that set `adPlatform`, so its `copy`/
  // `persisted` are always the matching platform's shape — TypeScript just
  // cannot see that correlation across a spread of a discriminated union.
  const advisoryRepairResult = await produced.runAdvisoryRepair();
  produced = {
    ...produced,
    ...advisoryRepairResult,
    // `runAdvisoryRepair` returns only `{ copy, warnings, persisted }` — the
    // outer `offerClass` (set once, before repair, at construction) is not
    // part of that return, so spreading it alone would refresh
    // `copy.offerClass` and leave the outer field pointing at the
    // pre-repair value. Every producer sets the outer field FROM
    // `copy.offerClass` at construction time (`offerClass:
    // ready.copy.offerClass`); keep that invariant true after a repair that
    // changed it too, rather than leaving a caller who reads the outer
    // field a stale answer.
    offerClass: advisoryRepairResult.copy.offerClass,
  } as ReadyBeforeAdvisoryRepair;

  // THE GATE'S REASONS ARE RECOMPUTED AGAINST THE OBJECT THAT ACTUALLY SHIPS.
  //
  // They used to accumulate into a shared array as each candidate was gated,
  // and `produced = retry` after the judge's rewrite then replaced the
  // candidate wholesale — so a reason about a DISCARDED draft rode out on
  // copy that never had the defect. Live on 2026-09-03 that shipped "No
  // claim in the primary text states its mechanism" on copy whose both
  // primary texts contained the word "because". That is this repo's own
  // "stale error ledger read as current state", and on a paid surface it
  // teaches a marketer to ignore the flag. Recomputing here is deterministic
  // and costs no model call, so the reasons are a property of `produced`,
  // not a history of everything that was tried.
  const gateReasons =
    produced.adPlatform === 'meta'
      ? [...new Set(gateNotes(produced.copy).map((note) => note.detail))].map(
          (detail) => `Anti-corny gate: ${detail}`,
        )
      : [];

  // Same "recompute against what actually ships" discipline as `gateReasons`
  // above — the doctrine pass above may have regenerated `produced` wholesale
  // (or spent its one attempt and left the original candidate standing
  // either way), so this is checked fresh against the final `produced.copy`,
  // never carried over from before the repair attempt.
  const evaluateFinalDoctrine = () =>
    evaluateDoctrine(produced as Extract<ProducerOutcome, { kind: 'ready' }>);
  let finalFindings = evaluateFinalDoctrine();

  // THE CLAIMS GATE. A blocking finding (an unqualified statistic, an
  // outcome promise, a legal/directive claim the evidence never made, a
  // brand's own avoided phrase) that is STILL here after the shared
  // regeneration budget must not ship as a draft with a footnote: a marketer
  // approving 3 concepts skims warnings, and "qualify for obsolescence
  // write-downs" or a bare "43%" in an approved ad is exactly the claim the
  // founder's claims rules name as must-not-ship. Two outcomes only — the
  // offending OPTIONAL field is dropped (a LinkedIn description is optional
  // reinforcement, never load-bearing), or the copy is refused with the exact
  // claim named. Never a silent pass, never a soft warning.
  // WITHHOLD, DON'T FOOTNOTE. A LinkedIn sentence the claims checks flagged (a
  // dropped hedge, an invented offer, a reader diagnosis) or the judge named as
  // unsupported, still here after the regeneration budget, is taken out of the
  // ad. A flagged sentence the copy cannot lose (the headline) or a commentary
  // too thin to run without it means no ad is saved.
  const withheldWarnings: GroundedAdCopyWarning[] = [];
  if (produced.adPlatform === 'linkedin') {
    // A results figure the copy states unattributed has a mechanical fix that
    // keeps the figure: the sentence the brand's own results tile supports,
    // attributed to the brand's report of one client. It is applied before
    // anything is withheld, so the strongest proof on file is not thrown away
    // for want of a regeneration the budget did not have.
    const attributions = finalFindings.filter(
      (finding) => finding.blocking && finding.sentence && finding.rewrite,
    );
    if (attributions.length > 0) {
      let commentary = produced.copy.commentary;
      let description = produced.copy.description;
      const applied: Array<{ from: string; to: string }> = [];
      for (const finding of attributions) {
        const from = finding.sentence as string;
        const to = finding.rewrite as string;
        if (commentary.includes(from)) {
          commentary = commentary.replace(from, to);
          applied.push({ from, to });
        } else if (description?.includes(from)) {
          description = description.replace(from, to);
          applied.push({ from, to });
        }
      }
      if (applied.length > 0) {
        const descriptionPart = description === undefined ? {} : { description };
        produced = {
          ...produced,
          copy: { ...produced.copy, commentary, ...descriptionPart },
          persisted: { ...produced.persisted, commentary, ...descriptionPart },
        } as unknown as ReadyBeforeAdvisoryRepair;
        finalFindings = evaluateFinalDoctrine();
        for (const { from, to } of applied) {
          withheldWarnings.push({
            field: 'claims_attributed',
            detail: `Rewrote "${from}" as "${to}" — the result is the brand's own reported figure, so it is stated as the brand's report of one client and the figure is kept.`,
            provenance:
              'src/lib/ad-copy/generate-grounded-ad-copy.ts — claims gate; a results figure is attributed, not dropped.',
          });
        }
      }
    }
    // A headline the claims checks refuse (a result figure it cannot carry, or a lead nothing on
    // file backs) has a mechanical fix when the Concept's own results tile is on file: a plain
    // label composed from that tile's words, with no figure to attribute. It is applied before the
    // ad is refused, because a headline cannot be withheld and the whole ad would be lost.
    const headlineFinding = finalFindings.find(
      (finding) =>
        /^headline/i.test(finding.field ?? '') &&
        (finding.blocking || finding.detail.includes('is not about this Concept')),
    );
    if (headlineFinding && request.conceptHook?.trim()) {
      const tile = verifiableSources.find(
        (source) => isResultTile(source) && isTileRelevantTo(source, request.conceptHook),
      );
      const composed = tile ? composeTileHeadline(tile) : null;
      const writtenHeadline = (produced.copy as GroundedLinkedInAdCopy).headline;
      if (composed && composed !== writtenHeadline) {
        const from = writtenHeadline;
        produced = {
          ...produced,
          copy: { ...produced.copy, headline: composed },
          persisted: { ...produced.persisted, headline: composed },
        } as unknown as ReadyBeforeAdvisoryRepair;
        finalFindings = evaluateFinalDoctrine();
        withheldWarnings.push({
          field: 'headline_composed',
          detail: `The headline "${from}" was replaced with "${composed}", composed from the Concept's own results line, because the written headline could not be repaired.`,
          provenance:
            'src/lib/ad-copy/generate-grounded-ad-copy.ts — claims gate; a headline is composed from the evidence rather than refusing the whole ad.',
        });
      }
    }
    const flagged = [
      ...finalFindings
        .filter((finding) => finding.blocking && finding.sentence)
        .map((finding) => ({ sentence: finding.sentence as string, field: finding.field })),
      ...judgeUnsupported.map((sentence) => ({ sentence, field: undefined })),
    ];
    if (flagged.length > 0) {
      const withheld = withholdFlaggedSentences(produced.copy as GroundedLinkedInAdCopy, flagged);
      if (withheld.unremovable.length > 0 || withheld.commentaryTooThin) {
        const named = [...withheld.unremovable, ...withheld.removed]
          .map((sentence) => `"${sentence}"`)
          .join('; ');
        return {
          kind: 'failure',
          code: 'claims_gate_blocked',
          message: `Copy held back by the claims gate: ${named} could not be repaired and cannot be removed without leaving no ad.`,
          userError: `The copy kept making a claim the evidence on file does not support (${named}), so none was saved.`,
          reasons: finalFindings
            .filter((finding) => finding.blocking)
            .map((finding) => finding.detail),
        };
      }
      if (withheld.removed.length > 0) {
        const { description: _oldDescription, ...copyRest } = produced.copy as Record<
          string,
          unknown
        >;
        const { description: _oldPersistedDescription, ...persistedRest } = produced.persisted;
        const description =
          withheld.description === undefined ? {} : { description: withheld.description };
        produced = {
          ...produced,
          copy: { ...copyRest, commentary: withheld.commentary, ...description },
          persisted: { ...persistedRest, commentary: withheld.commentary, ...description },
        } as unknown as ReadyBeforeAdvisoryRepair;
        finalFindings = evaluateFinalDoctrine();
        for (const sentence of withheld.removed) {
          withheldWarnings.push({
            field: 'claims_withheld',
            detail: `Removed from the ad: "${sentence}" — the evidence on file does not support it and it could not be repaired within the regeneration budget.`,
            provenance:
              'src/lib/ad-copy/generate-grounded-ad-copy.ts — claims gate; flagged sentences are withheld, not footnoted.',
          });
        }
      }
    }
  }
  if (produced.kind === 'ready') {
    const crossModel = await findUnstatedClaimsCrossModel({
      adText: renderCopyForReview(produced as Extract<ProducerOutcome, { kind: 'ready' }>),
      facts: verifiableSources,
    });
    if (crossModel.length > 0)
      judgeUnsupported = [...new Set([...judgeUnsupported, ...crossModel])];
  }
  // Meta and Google: a sentence or asset the claims review says the evidence does not state is
  // taken out before saving, as LinkedIn already does, when enough copy is left to run.
  if (produced.kind === 'ready' && judgeUnsupported.length > 0) {
    const removedHere: string[] = [];
    const bare = (value: string) =>
      value
        .trim()
        .replace(/[.!?]+$/, '')
        .toLowerCase();
    const flaggedIn = (text: string) =>
      judgeUnsupported.filter(
        (phrase) =>
          holdsFlaggedPhrase(text, phrase) ||
          bare(text) === bare(phrase) ||
          (bare(phrase).length > 12 && bare(text).includes(bare(phrase))),
      );
    if (produced.adPlatform === 'meta') {
      const meta = produced.copy as GroundedMetaAdCopy;
      const primaryTexts = meta.primaryTexts.map((entry) => {
        const hits = flaggedIn(entry.text);
        if (hits.length === 0) return entry;
        const out = withoutFlaggedPhrases(entry.text, hits);
        if (out.unsafe || out.text.trim().length < 40) return entry;
        removedHere.push(...out.removed);
        return { ...entry, text: out.text.trim() };
      }) as GroundedMetaAdCopy['primaryTexts'];
      const headlines = meta.headlines.map((entry) => {
        if (flaggedIn(entry.text).length === 0) return entry;
        const plain = request.product.trim().slice(0, 40);
        if (!plain || plain === entry.text) return entry;
        removedHere.push(entry.text);
        return { ...entry, text: plain };
      }) as GroundedMetaAdCopy['headlines'];
      if (removedHere.length > 0) {
        const persisted = produced.persisted as Record<string, unknown>;
        produced = {
          ...produced,
          copy: { ...meta, primaryTexts, headlines },
          persisted: {
            ...persisted,
            ...('primaryTexts' in persisted ? { primaryTexts } : {}),
            ...('headlines' in persisted ? { headlines } : {}),
          },
        } as unknown as ReadyBeforeAdvisoryRepair;
      }
    } else if (produced.adPlatform === 'google_rsa') {
      const rsa = produced.copy as GroundedGoogleRsaAdCopy;
      const keep = <T extends { text: string }>(items: readonly T[], min: number): T[] => {
        const out: T[] = [];
        let room = items.length - min;
        for (const item of items) {
          const hits = flaggedIn(item.text);
          if (hits.length > 0 && room > 0) {
            room -= 1;
            removedHere.push(item.text);
            continue;
          }
          out.push(item);
        }
        return out;
      };
      const headlines = keep(rsa.headlines, 3);
      const descriptions = keep(rsa.descriptions, 2);
      if (removedHere.length > 0) {
        const persisted = produced.persisted as Record<string, unknown>;
        produced = {
          ...produced,
          copy: { ...rsa, headlines, descriptions },
          persisted: {
            ...persisted,
            ...('headlines' in persisted ? { headlines } : {}),
            ...('descriptions' in persisted ? { descriptions } : {}),
          },
        } as unknown as ReadyBeforeAdvisoryRepair;
      }
    }
    if (removedHere.length > 0) {
      finalFindings = evaluateFinalDoctrine();
      for (const sentence of [...new Set(removedHere)]) {
        withheldWarnings.push({
          field: 'claims_withheld',
          detail: `Removed from the ad: "${sentence}" — the evidence on file does not state it.`,
          provenance:
            'src/lib/ad-copy/generate-grounded-ad-copy.ts — claims review; unsupported sentences are withheld on every channel.',
        });
      }
      judgeUnsupported = judgeUnsupported.filter(
        (phrase) => !removedHere.some((removed) => holdsFlaggedPhrase(removed, phrase)),
      );
    }
  }
  const unresolvedUnsupported = judgeUnsupported.filter(
    (assertion) =>
      !withheldWarnings.some((warning) => warning.detail.includes(`"${assertion.trim()}"`)),
  );
  if (unresolvedUnsupported.length > 0) {
    judgeReasons.push({
      field: 'needs_review',
      detail: `Claims review: the copy still asserts ${unresolvedUnsupported.length === 1 ? 'something' : `${unresolvedUnsupported.length} things`} the evidence on file does not state — ${unresolvedUnsupported.map((assertion) => `"${assertion}"`).join('; ')}.`,
    });
  }

  let droppedDescription = false;
  const blockingBeforeDrop = finalFindings.filter((finding) => finding.blocking);
  if (
    blockingBeforeDrop.length > 0 &&
    produced.adPlatform === 'linkedin' &&
    blockingBeforeDrop.every((finding) => finding.field === 'description')
  ) {
    const { description: _droppedCopyDescription, ...copyWithoutDescription } = produced.copy;
    const { description: _droppedPersistedDescription, ...persistedWithoutDescription } =
      produced.persisted;
    produced = {
      ...produced,
      copy: copyWithoutDescription,
      persisted: persistedWithoutDescription,
    } as ReadyBeforeAdvisoryRepair;
    droppedDescription = true;
    finalFindings = evaluateFinalDoctrine();
  }
  const blockingFindings = finalFindings.filter((finding) => finding.blocking);
  if (blockingFindings.length > 0) {
    const details = blockingFindings.map((finding) => finding.detail);
    return {
      kind: 'failure',
      code: 'claims_gate_blocked',
      message: `Copy held back by the claims gate after ${MAX_COPY_REGENERATIONS} regeneration attempts: ${details.join(' ')}`,
      userError: `The copy kept making a claim the evidence on file does not support, so none was saved. ${details.join(' ')}`,
      reasons: details,
    };
  }
  const finalFields = shippingFields(produced.adPlatform, produced.copy);
  const doctrineReasons = finalFindings.map((finding) => finding.detail);

  const needsReviewReasons: Array<{ field: 'needs_review' | 'judge_incomplete'; detail: string }> =
    [
      ...gateReasons.map((detail) => ({ field: 'needs_review' as const, detail })),
      ...doctrineReasons.map((detail) => ({ field: 'needs_review' as const, detail })),
      ...judgeReasons,
    ];

  const needsReview = needsReviewReasons.length > 0;
  const warnings = [
    ...produced.warnings,
    ...needsReviewReasons.map((reason) => ({
      field: reason.field,
      detail: reason.detail,
      provenance:
        reason.field === 'judge_incomplete'
          ? 'src/lib/ad-copy/generate-grounded-ad-copy.ts — automated creative-review judge, retried once, still did not complete.'
          : produced.adPlatform === 'meta'
            ? 'src/lib/ad-copy/generate-grounded-ad-copy.ts — deterministic anti-corny gate, quality-doctrine checks, and automated creative review, not a human sign-off.'
            : 'src/lib/ad-copy/generate-grounded-ad-copy.ts — quality-doctrine checks and automated creative review, not a human sign-off.',
    })),
    ...(droppedDescription
      ? [
          {
            field: 'description',
            detail:
              'The description was removed: it made a claim the evidence on file does not support and could not be repaired within the regeneration budget. LinkedIn runs without one.',
            provenance:
              'src/lib/ad-copy/generate-grounded-ad-copy.ts — claims gate; the description is optional on LinkedIn.',
          },
        ]
      : []),
    ...withheldWarnings,
    ...findUnevidencedFiguresInAdCopy(finalFields, evidenceCorpus),
  ];
  produced.copy.warnings = warnings;
  // `runAdvisoryRepair` is this module's own internal closure — see
  // `ReadyBeforeAdvisoryRepair` — and must never cross the public boundary.
  const { runAdvisoryRepair: _runAdvisoryRepair, ...publicProduced } = produced;
  timings.push({ step: 'total', ms: Math.max(0, Date.now() - engineStartedAt) });
  return {
    ...publicProduced,
    warnings,
    needsReview,
    review: toStructuredAdReview(review, AD_COPY_HOOK_SCORE_BAR),
    timings: [...timings],
  };
}
